import * as signalR from '@microsoft/signalr';
import { v4 as uuidv4 } from 'uuid';

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
	AddCircle as AddIcon,
	AttachFile as AttachFileIcon,
	ArrowDownward as ArrowDownwardIcon,
	ArrowUpward as ArrowUpwardIcon,
	Check as CheckIcon,
	ChevronRight as CollapseIcon,
	Close as CloseIcon,
	Delete as DeleteIcon,
	Edit as EditIcon,
	ExpandMore as ExpandMoreIcon,
	GroupAdd as GroupAddIcon,
	History as HistoryIcon,
	Language as LanguageIcon,
	Mic as MicIcon,
	MoreVert as MoreVertIcon,
	OpenInFull as OpenInFullIcon,
	PersonRemove as PersonRemoveIcon,
	Replay as RetryIcon,
	Refresh as RefreshIcon,
	Share as ShareIcon,
	StopCircle as StopIcon,
	Visibility as PreviewIcon,
} from '@mui/icons-material';
import {
	Alert,
	Autocomplete,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { alpha, darken, keyframes, lighten, type SxProps, type Theme } from '@mui/material/styles';

import { TenantUserModel, UserClient } from '../../api/api';
import { useApiClient } from '../../common';
import { appShape } from '../../themes/shapeTokens';
import { useChangeSet } from '../ChangeSetContext';
import ChatMessageContent from './ChatMessageContent';
import { getChatAttribute } from './ChatMessageAttributes';
import { isChangeChatMessage } from './isChangeChatMessage';
import { ChatHtmlResolver } from './ChatHtmlFileContent';
import { formatTimestampFull, formatTimestampRelative } from '../../common/formatTimestamp';
import { usePersistedSetting } from '../../common/hooks/usePersistedSetting';
import useSpeechRecognition from '../../common/hooks/useSpeechRecognition';
import { useTimer } from '../../common/hooks/useTimer';
import CopyToClipboardButton from '../Clipboard/CopyToClipboardButton';
import DialogHeaderActions from '../DialogHeaderActions';
import { ConversationEventNotification, useNotifications } from '../NotificationContext';
import useNotificationRefetch from '../useNotificationRefetch';
import useNotificationSubscription from '../useNotificationSubscription';
import {
	AttachmentPreviewSelection,
	AttachmentUrlResolver,
	ChatAttachmentPreviewDialog,
	ChatAttachmentThumbnail,
	getAttachmentPreviewKind,
} from './ChatAttachmentPreview';
import { ToolProgressGroup, buildChatDisplayItems, isPartialChatMessage } from './ChatProgress';
import { ChatAgentOption, ChatAttachment, ChatMentionSuggestion, ChatMessage, ChatParticipant, ChatSessionSummary, PromptChatAdapter } from './PromptChatAdapter';
import usePromptChatDeltaLoader, { CHAT_RESPONSE } from './usePromptChatDeltaLoader';
import ChatAgentAvatar from './ChatAgentAvatar';
import usePromptChatContext from './usePromptChatContext';

const SESSION_PAGE_SIZE = 50;
const SESSION_SCROLL_THRESHOLD_PX = 80;
const MESSAGE_PAGE_SIZE = 50;
const MESSAGE_SCROLL_TOP_THRESHOLD_PX = 80;
const INITIAL_SESSION_NAME_MAX_LENGTH = 25;
const SHARE_USER_SEARCH_DEBOUNCE_MS = 250;
const SHARE_USER_SEARCH_PAGE_SIZE = 50;
const CHAT_DIALOG_MAXIMIZED_STORAGE_KEY = 'promptChat.dialog.maximized';
const CHAT_MODE_STORAGE_KEY = 'promptChat.mode';
const SPEECH_RECOGNITION_LANGUAGE_STORAGE_KEY = 'promptChat.speechRecognitionLanguage';
const DOMAIN_MODELER_AGENT_NAME = 'Domain Modeller';
const SESSION_MESSAGE_CACHE_LIMIT = 8;
const BOTTOM_SCROLL_SETTLE_FRAMES = 8;
const MAX_ATTACHMENT_COUNT = 5;
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 50 * 1024 * 1024;
const ATTACHMENT_URL_CACHE_DURATION_MS = 4 * 60 * 1000;
const RELATIVE_TIMESTAMP_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const TIMESTAMP_REFRESH_INTERVAL_MS = 60 * 1000;
const TERMINAL_CONVERSATION_STATUSES = new Set(['completed', 'failed']);
const CHAT_ATTACHMENT_ACCEPT = '.txt,.md,.csv,.json,.xml,.pdf,image/png,image/jpeg,image/gif,image/webp';
const SUPPORTED_ATTACHMENT_TYPES = new Set([
	'application/json',
	'application/pdf',
	'application/xml',
	'image/gif',
	'image/jpeg',
	'image/png',
	'image/webp',
	'text/csv',
	'text/markdown',
	'text/plain',
	'text/xml',
]);
const activeMicrophoneHalo = keyframes`
	0% {
		transform: scale(0.78);
		opacity: 0.9;
	}
	100% {
		transform: scale(1.7);
		opacity: 0;
	}
`;
const composerActionProgress = keyframes`
	to { transform: rotate(360deg); }
`;
const progressingComposerActionSx = (color: string) => ({
	position: 'relative',
	overflow: 'visible',
	'&::after': {
		content: '""',
		position: 'absolute',
		inset: -3,
		borderRadius: '50%',
		border: `2px solid ${alpha(color, 0.2)}`,
		borderTopColor: color,
		borderRightColor: color,
		animation: `${composerActionProgress} 1s linear infinite`,
		// Functional progress must remain visible despite global reduced-motion animation resets.
		animationDuration: '1s !important',
		animationIterationCount: 'infinite !important',
		pointerEvents: 'none',
	},
	'@media (prefers-reduced-motion: reduce)': {
		'&::after': { animationDuration: '1.6s !important' },
	},
} as const);
const speechRecognitionLanguageOptions = [
	{ code: 'pl-PL', label: 'Polski' },
	{ code: 'en-US', label: 'English (US)' },
	{ code: 'en-GB', label: 'English (UK)' },
	{ code: 'ja-JP', label: '日本語' },
	{ code: 'de-DE', label: 'Deutsch' },
	{ code: 'es-ES', label: 'Español' },
	{ code: 'fr-FR', label: 'Français' },
	{ code: 'it-IT', label: 'Italiano' },
];

const getDefaultSpeechRecognitionLanguage = () =>
	typeof navigator === 'undefined' ? 'en-US' : navigator.language || 'en-US';
export type ChatMode = 'Design' | 'Implement';
export type PromptChatDraftRequest = { id: string; prompt: string; mode?: ChatMode };
type PendingAttachment = {
	localId: string;
	file: File;
	previewUrl: string;
	attachment?: ChatAttachment;
	status: 'uploading' | 'uploaded' | 'failed';
	error?: string;
};
type ActiveAttachmentPreview = {
	selection: AttachmentPreviewSelection;
	resolveUrl: AttachmentUrlResolver;
};
const DEFAULT_CHAT_MODE: ChatMode = 'Design';
const getChatModeColor = (theme: Theme, mode: ChatMode) => (mode === 'Implement' ? theme.palette.warning.main : theme.palette.primary.main);
const randolAvatarSx = {
	bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
	border: (theme: Theme) => `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
};
const headerActionSx = {
	color: 'text.secondary',
	borderRadius: appShape.controlRadius,
	'&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
};
const composerIconButtonSx = { width: 32, height: 32, flexShrink: 0, color: 'text.secondary' };

const getPreferredNewChatAgentId = (agents: ChatAgentOption[]) =>
	agents.find((agent) => agent.name.localeCompare(DOMAIN_MODELER_AGENT_NAME, undefined, { sensitivity: 'accent' }) === 0)?.id ??
	agents.find((agent) => agent.isDefault)?.id ??
	agents[0]?.id;

const mergeChatMessages = (cachedMessages: ChatMessage[], latestMessages: ChatMessage[]) => {
	const messageIds = new Set(cachedMessages.map((message) => message.id));
	return [...cachedMessages, ...latestMessages.filter((message) => !messageIds.has(message.id))];
};

const formatChatMessageTimestamp = (timestamp: string, now: number) =>
	now - new Date(timestamp).getTime() > RELATIVE_TIMESTAMP_MAX_AGE_MS
		? formatTimestampFull(timestamp)
		: formatTimestampRelative(timestamp);

const MemoizedToolProgressGroup = React.memo(ToolProgressGroup);

const formatAttachmentSize = (size?: number) => {
	if (!size) return '';
	return size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(size / 1024)} KB`;
};

const getTokenCount = (attributes: ChatMessage['attributes'], key: string): number | undefined => {
	const value = getChatAttribute(attributes, key)?.trim();
	if (!value || !/^\d+$/.test(value)) return undefined;
	const count = Number(value);
	return Number.isSafeInteger(count) ? count : undefined;
};

const getTokenUsageDisplay = (attributes: ChatMessage['attributes']) => {
	const input = getTokenCount(attributes, 'usage:inputTokens');
	const output = getTokenCount(attributes, 'usage:outputTokens');
	const cachedInput = getTokenCount(attributes, 'usage:cachedInputTokens');
	const reasoning = getTokenCount(attributes, 'usage:reasoningTokens');
	const total = getTokenCount(attributes, 'usage:totalTokens');
	const source = getChatAttribute(attributes, 'usage:source')?.trim().toLowerCase();
	const format = (count: number) => count.toLocaleString();
	const visible = [
		input !== undefined ? `${format(input)} in` : undefined,
		output !== undefined ? `${format(output)} out` : undefined,
		total !== undefined && input === undefined && output === undefined ? `${format(total)} total` : undefined,
	].filter(Boolean).join(' · ');
	if (!visible) return undefined;

	const details = [
		input !== undefined ? `Input: ${format(input)}` : undefined,
		output !== undefined ? `Output: ${format(output)}` : undefined,
		cachedInput !== undefined
			? `Cached input: ${format(cachedInput)}${input !== undefined && input > 0 ? ` (${((cachedInput / input) * 100).toFixed(1)}% of input)` : ''}`
			: undefined,
		reasoning !== undefined ? `Reasoning: ${format(reasoning)}` : undefined,
		total !== undefined ? `Total: ${format(total)}` : undefined,
	].filter(Boolean).join(' · ');
	return { visible: `${visible} tokens`, details: `${source === 'provider' ? 'Provider-reported token usage' : 'Token usage'}. ${details}. Input includes the full model context.` };
};

const getAttachmentContentType = (file: File) => {
	if (file.type) return file.type.toLowerCase();
	const extension = file.name.split('.').pop()?.toLowerCase();
	return extension === 'json'
		? 'application/json'
		: extension === 'pdf'
			? 'application/pdf'
			: extension === 'xml'
				? 'application/xml'
				: extension === 'csv'
					? 'text/csv'
					: extension === 'md'
						? 'text/markdown'
						: extension === 'txt'
							? 'text/plain'
							: '';
};

const ChatMessageBubble = React.memo(function ChatMessageBubble({
	message,
	timestampNow,
	onPreviewAttachment,
	resolveAttachmentUrl,
	resolveHtml,
}: {
	message: ChatMessage;
	timestampNow: number;
	onPreviewAttachment?: (attachment: ChatAttachment, attachments: ChatAttachment[]) => void;
	resolveAttachmentUrl?: (attachment: ChatAttachment) => Promise<string>;
	resolveHtml?: ChatHtmlResolver;
}) {
	const isUser = message.role === 'user';
	const tokenUsage = isUser ? undefined : getTokenUsageDisplay(message.attributes);
	const [tokenUsageExpanded, setTokenUsageExpanded] = useState(false);
	const isPartial = isPartialChatMessage(message);
	const messageTime = formatChatMessageTimestamp(message.createdDate, timestampNow);
	const messageAge = timestampNow - new Date(message.createdDate).getTime();
	const isRecentlyArrived = messageAge >= 0 && messageAge < 3000;
	const isChange = isChangeChatMessage(message);
	const htmlFileIds = new Set(message.contents?.filter((content) => ['html', 'sandboxed-html-v1'].includes(content.contentType.trim().toLowerCase())).map((content) => content.fileId));
	const attachments = (message.attachments ?? []).filter((attachment) => !htmlFileIds.has(attachment.fileId));
	const imageAttachments = attachments.filter((attachment) => getAttachmentPreviewKind(attachment) === 'image');
	const fileAttachments = resolveAttachmentUrl ? attachments.filter((attachment) => getAttachmentPreviewKind(attachment) !== 'image') : attachments;

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: isUser ? 'flex-end' : 'flex-start',
				mb: 1,
				alignItems: 'flex-start',
				opacity: isPartial ? 0.68 : 1,
				animation: !isPartial && isRecentlyArrived
					? isChange
						? 'chat-message-change 900ms ease-out'
						: 'chat-message-arrive 180ms ease-out'
					: undefined,
				'@keyframes chat-message-arrive': {
					from: { opacity: 0, transform: 'translateY(6px)' },
					to: { opacity: 1, transform: 'translateY(0)' },
				},
				'@keyframes chat-message-change': {
					'0%': { filter: 'drop-shadow(0 0 5px rgba(46, 125, 50, 0.72))' },
					'100%': { filter: 'drop-shadow(0 0 0 transparent)' },
				},
				contentVisibility: 'auto',
				containIntrinsicSize: 'auto 100px',
			}}
		>
			<Box
				sx={{
					maxWidth: '90%',
					minWidth: 0,
					bgcolor: isUser ? 'primary.main' : 'background.default',
					color: isUser ? 'primary.contrastText' : 'text.secondary',
					px: 1.25,
					py: 0.75,
					borderRadius: 2,
					fontSize: isPartial ? '0.76rem' : '0.8rem',
					border: '1px solid',
					borderLeftWidth: isPartial ? 3 : 1,
					borderColor: isPartial ? 'text.disabled' : isUser ? 'primary.main' : 'divider',
					boxShadow: isUser ? 1 : 'none',
					overflowWrap: 'break-word',
					wordBreak: 'break-word',
				}}
			>
				<ChatMessageContent message={message} resolveHtml={resolveHtml} resolveAttachmentUrl={resolveAttachmentUrl} />
				{attachments.length > 0 && (
					<Box sx={{ display: 'grid', gap: 0.5, mt: message.text ? 0.75 : 0 }}>
						{resolveAttachmentUrl && onPreviewAttachment && imageAttachments.length > 0 && (
							<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))', gap: 0.5 }}>
								{imageAttachments.map((attachment) => (
									<ChatAttachmentThumbnail
										key={attachment.fileId}
										attachment={attachment}
										resolveUrl={resolveAttachmentUrl}
										onClick={() => onPreviewAttachment(attachment, attachments)}
									/>
								))}
							</Box>
						)}
						{fileAttachments.map((attachment) => (
							<Button
								key={attachment.fileId}
								size="small"
								variant="outlined"
								startIcon={<AttachFileIcon fontSize="small" />}
								onClick={() => onPreviewAttachment?.(attachment, attachments)}
								disabled={!onPreviewAttachment}
								sx={{
									justifyContent: 'flex-start',
									textTransform: 'none',
									color: isUser ? 'primary.contrastText' : 'text.primary',
									borderColor: isUser ? 'rgba(255,255,255,0.48)' : 'divider',
								}}
							>
								<Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{attachment.fileName}{attachment.size ? ` · ${formatAttachmentSize(attachment.size)}` : ''}
								</Box>
							</Button>
						))}
					</Box>
				)}
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 20, mt: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
						{message.agentName && !isUser && (
							<Typography variant="caption" sx={{ opacity: 0.6, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								{message.agentName}
							</Typography>
						)}
						{tokenUsage && (
							<Box
								component="button"
								type="button"
								aria-expanded={tokenUsageExpanded}
								aria-controls={`token-usage-${message.id}`}
								aria-label={`${tokenUsageExpanded ? 'Hide' : 'Show'} token usage details`}
								onClick={() => setTokenUsageExpanded((expanded) => !expanded)}
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: 0.25,
									p: 0,
									border: 0,
									background: 'transparent',
									color: 'inherit',
									font: 'inherit',
									textAlign: 'left',
									cursor: 'pointer',
									opacity: 0.7,
								}}
							>
								<ExpandMoreIcon sx={{ fontSize: 14, transform: tokenUsageExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
								<Typography component="span" variant="caption" sx={{ fontSize: '0.66rem', overflowWrap: 'anywhere' }}>
									{tokenUsage.visible}
								</Typography>
							</Box>
						)}
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
						<Typography className="chat-message-time" variant="caption" title={formatTimestampFull(message.createdDate)} sx={{ opacity: 0.7, fontSize: '0.66rem' }}>
							{messageTime}
						</Typography>
						<CopyToClipboardButton
							value={message.text}
							title="Copy message"
							disabled={!message.text}
							edge={false}
							iconSize={14}
							sx={{ color: isUser ? 'primary.contrastText' : 'text.secondary', p: 0.25 }}
						/>
					</Box>
				</Box>
				{tokenUsage && (
					<Collapse in={tokenUsageExpanded} timeout="auto">
						<Box
							id={`token-usage-${message.id}`}
							sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}
						>
							<Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: '0.66rem', overflowWrap: 'anywhere' }}>
								{tokenUsage.details}
							</Typography>
						</Box>
					</Collapse>
				)}
			</Box>
		</Box>
	);
});

export interface PromptChatProps {
	/** Fallback title before a named conversation is selected. */
	defaultTitle?: string;
	/** Host controls appended to the shared header, hidden in the expanded chat dialog. */
	headerActions?: React.ReactNode;
	/** Optional host context panel inside the composer, between the message input and toolbar. */
	composerContext?: React.ReactNode;
	/** Optional host controls before the attachment button in the composer toolbar. */
	composerLeadingActions?: React.ReactNode;
	/** Host task actions placed after new/history and before overflow/window controls. */
	headerPrimaryActions?: React.ReactNode;
	headerIcon?: React.ReactNode;
	headerSx?: SxProps<Theme>;
	/** Keep the host title fixed and show the editable conversation name on its own row. */
	showConversationNameBar?: boolean;
	/** Use the selected/session agent name instead of the host title. */
	showAgentNameInHeader?: boolean;
	onStatusChange?: (status: PromptChatStatus) => void;
	adapter: PromptChatAdapter;
	area?: string;
	emptyStateText?: string;
	suggestedPrompts?: string[];
	unavailableText?: string;
	onComplete?: () => void | Promise<void>;
	onAssistantMessageReceived?: (message: ChatMessage) => void | Promise<void>;
	onDialogOpenChange?: (isOpen: boolean) => void;
	onDismiss?: () => void;
	draftRequest?: PromptChatDraftRequest;
}

export type PromptChatStatus = { busy: boolean; failed: boolean; sessionId?: string };

const PromptChat: React.FC<PromptChatProps> = ({
	adapter,
	area,
	emptyStateText,
	suggestedPrompts = [],
	unavailableText,
	onComplete,
	onAssistantMessageReceived,
	onDialogOpenChange,
	onDismiss,
	draftRequest,
	onStatusChange,
	defaultTitle = 'Ask Randol',
	headerActions,
	composerContext,
	composerLeadingActions,
	headerPrimaryActions,
	headerIcon,
	headerSx = [],
	showConversationNameBar = false,
	showAgentNameInHeader = false,
}) => {
	const { currentChangeSetId } = useChangeSet();
	const { connectionState, latestConversationEvent } = useNotifications();
	const userClient = useApiClient(UserClient);
	const [sessionCreatedDate, setSessionCreatedDate] = useState<string | null>(null);
	const [sessionName, setSessionName] = useState<string | null>(null);
	const [sessionAgentName, setSessionAgentName] = useState<string | undefined>();
	const [sessionIsNew, setSessionIsNew] = useState(true);
	const [sessionLoading, setSessionLoading] = useState(true);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [hasMoreMessages, setHasMoreMessages] = useState(false);
	const [oldestMessageSequence, setOldestMessageSequence] = useState<number | undefined>(undefined);
	const [loadingEarlierMessages, setLoadingEarlierMessages] = useState(false);
	const [timestampNow, setTimestampNow] = useState(() => Date.now());
	const [input, setInput] = useState('');
	const [chatMode, setChatMode] = usePersistedSetting<ChatMode>(CHAT_MODE_STORAGE_KEY, DEFAULT_CHAT_MODE);
	useTimer({
		callback: () => setTimestampNow(Date.now()),
		delayMs: TIMESTAMP_REFRESH_INTERVAL_MS,
		mode: 'interval',
	});
	const adapterSessionKey = adapter.sessionKey ?? adapter;
	const attachmentAdapterRef = useRef({ key: adapterSessionKey, adapter });
	useLayoutEffect(() => {
		attachmentAdapterRef.current = { key: adapterSessionKey, adapter };
	}, [adapter, adapterSessionKey]);

	useEffect(() => {
		if (!draftRequest) {
			return;
		}

		setInput(draftRequest.prompt);
		if (draftRequest.mode) {
			setChatMode(draftRequest.mode);
		}
	}, [draftRequest]);
	const [speechLanguage, setSpeechLanguage] = usePersistedSetting(
		SPEECH_RECOGNITION_LANGUAGE_STORAGE_KEY,
		getDefaultSpeechRecognitionLanguage(),
	);
	const [speechLanguageAnchorEl, setSpeechLanguageAnchorEl] = useState<HTMLElement | null>(null);
	const [sending, setSending] = useState(false);
	const [isPolling, setIsPolling] = useState(false);
	const [errorBanner, setErrorBanner] = useState<string | null>(null);
	const [sessionPickerOpen, setSessionPickerOpen] = useState(false);
	const [sessionPickerLoading, setSessionPickerLoading] = useState(false);
	const [sessionPickerError, setSessionPickerError] = useState<string | null>(null);
	const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
	const [sessionsPageIndex, setSessionsPageIndex] = useState(0);
	const [sessionsHasNextPage, setSessionsHasNextPage] = useState(false);
	const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
	const [renamingName, setRenamingName] = useState('');
	const [renamingSaving, setRenamingSaving] = useState(false);
	const [deleteConfirmSessionId, setDeleteConfirmSessionId] = useState<string | null>(null);
	const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
	const [headerRenaming, setHeaderRenaming] = useState(false);
	const [headerRenameName, setHeaderRenameName] = useState('');
	const [headerRenameSaving, setHeaderRenameSaving] = useState(false);
	const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [refreshingSession, setRefreshingSession] = useState(false);
	const [agentOptions, setAgentOptions] = useState<ChatAgentOption[]>([]);
	const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(undefined);
	const [chatDialogOpen, setChatDialogOpen] = useState(false);
	const [chatDialogMaximized, setChatDialogMaximized] = usePersistedSetting(CHAT_DIALOG_MAXIMIZED_STORAGE_KEY, true);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const [shareUsers, setShareUsers] = useState<TenantUserModel[]>([]);
	const [shareUserSearchText, setShareUserSearchText] = useState('');
	const [selectedShareUsers, setSelectedShareUsers] = useState<TenantUserModel[]>([]);
	const [sharedParticipants, setSharedParticipants] = useState<ChatParticipant[]>([]);
	const [sharedParticipantsLoading, setSharedParticipantsLoading] = useState(false);
	const [removingParticipantId, setRemovingParticipantId] = useState<string | null>(null);
	const [shareUsersLoading, setShareUsersLoading] = useState(false);
	const [shareSaving, setShareSaving] = useState(false);
	const [shareError, setShareError] = useState<string | null>(null);
	const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
	const [attachmentPreview, setAttachmentPreview] = useState<ActiveAttachmentPreview | null>(null);

	// @mention state
	const [mentionQuery, setMentionQuery] = useState<string | null>(null);
	const [mentionSuggestions, setMentionSuggestions] = useState<ChatMentionSuggestion[]>([]);
	const [showJumpToLatest, setShowJumpToLatest] = useState(false);

	const inputRef = useRef<HTMLTextAreaElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const messagesContainerRef = useRef<HTMLDivElement | null>(null);
	const isNearMessageBottomRef = useRef(true);
	const loadingEarlierMessagesRef = useRef(false);
	const autoPrefetchedHistorySessionIdRef = useRef<string | undefined>(undefined);
	const bottomScrollAnimationFrameRef = useRef<number | undefined>(undefined);
	const mentionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const previousConnectionStateRef = useRef(connectionState);
	const sessionPickerLoadingRef = useRef(false);
	const sessionNameManuallyChangedRef = useRef(false);
	const sessionResolutionRequestRef = useRef(0);
	const retrySendRef = useRef<{ key: string; requestId: string }>();
	const sessionMessagesCacheRef = useRef(new Map<string, ChatMessage[]>());
	const attachmentUrlCacheRef = useRef(new Map<string, { url: string; resolvedAt: number }>());
	useLayoutEffect(() => { attachmentUrlCacheRef.current.clear(); }, [adapterSessionKey]);
	const pendingPreviewUrlsRef = useRef(new Set<string>());
	const {
		responseStatus,
		responseTurnId,
		currentSessionId,
		currentSessionIdRef,
		loadMessagesDelta,
		notifyCompletedAssistantMessages,
		resetSessionTracking,
		setCurrentSession,
		setLastMessageFromMessages,
		stopWaiting,
	} = usePromptChatDeltaLoader({
		adapter,
		pushConnected: connectionState === signalR.HubConnectionState.Connected,
		onComplete,
		onAssistantMessageReceived,
		setErrorBanner,
		setIsWaiting: setIsPolling,
		setMessages,
	});
	useNotificationSubscription('conversation', currentSessionId);
	const [stopPending, setStopPending] = useState(false);
	const stopAgent = useCallback(async () => {
		if (!adapter.cancelResponse) {
			stopWaiting();
			return;
		}
		if (!currentSessionId || !responseTurnId || stopPending) return;
		setStopPending(true);
		try {
			await adapter.cancelResponse(currentSessionId, responseTurnId);
			await loadMessagesDelta(currentSessionId);
		} catch (error) {
			if (currentSessionIdRef.current === currentSessionId) {
				setErrorBanner(error instanceof Error ? error.message : 'Could not stop the agent. Please retry.');
			}
		} finally {
			setStopPending(false);
		}
	}, [adapter, currentSessionId, currentSessionIdRef, responseTurnId, stopPending, loadMessagesDelta, stopWaiting]);

	const cacheSessionMessages = useCallback((sessionId: string | undefined, sessionMessages: ChatMessage[]) => {
		if (!sessionId) {
			return;
		}

		const cache = sessionMessagesCacheRef.current;
		cache.delete(sessionId);
		cache.set(sessionId, sessionMessages);
		while (cache.size > SESSION_MESSAGE_CACHE_LIMIT) {
			const oldestSessionId = cache.keys().next().value;
			if (!oldestSessionId) {
				return;
			}

			cache.delete(oldestSessionId);
		}
	}, []);

	// Resolve/create session
	const resolveSession = useCallback(
		async (forceNew: boolean, agentId?: string) => {
			const requestId = ++sessionResolutionRequestRef.current;
			setSessionLoading(true);
			setErrorBanner(null);
			try {
				const s = await adapter.createOrGetSession(forceNew, agentId);
				if (requestId !== sessionResolutionRequestRef.current) {
					return;
				}

				setSessionCreatedDate(s.createdDate);
				setSessionName(s.name ?? null);
				setSessionAgentName(s.agentName);
				sessionNameManuallyChangedRef.current = false;
				setSessionIsNew(s.isNew);
				setSelectedAgentId((currentAgentId) => s.agentId ?? agentId ?? currentAgentId);
				setCurrentSession(s.sessionId);
				const msgs = s.messages ?? [];
				setHasMoreMessages(s.hasMoreMessages ?? false);
				setOldestMessageSequence(s.oldestMessageSequence);
				isNearMessageBottomRef.current = true;
				setShowJumpToLatest(false);
				setMessages(msgs);
				cacheSessionMessages(s.sessionId, msgs);
				setLastMessageFromMessages(msgs);
				await notifyCompletedAssistantMessages(msgs);
			} catch (err) {
				if (requestId === sessionResolutionRequestRef.current) {
					console.error('Failed to resolve session:', err);
				}
			} finally {
				if (requestId === sessionResolutionRequestRef.current) {
					setSessionLoading(false);
				}
			}
		},
		[adapter, cacheSessionMessages, notifyCompletedAssistantMessages, setCurrentSession, setLastMessageFromMessages],
	);

	const loadSessions = useCallback(
		async (pageIndex: number) => {
			if (!adapter.listSessions) return;
			if (sessionPickerLoadingRef.current) return;

			sessionPickerLoadingRef.current = true;
			setSessionPickerLoading(true);
			setSessionPickerError(null);
			try {
				const result = await adapter.listSessions(pageIndex, SESSION_PAGE_SIZE);
				const nextItems = result?.items ?? [];
				setSessions((prev) => {
					if (pageIndex === 0) return nextItems;

					const seenIds = new Set(prev.map((session) => session.sessionId));
					return [...prev, ...nextItems.filter((session) => !seenIds.has(session.sessionId))];
				});
				setSessionsPageIndex(pageIndex);
				setSessionsHasNextPage(result?.hasNextPage ?? false);
			} catch (err) {
				console.error('Failed to load chat sessions:', err);
				setSessionPickerError('Failed to load previous sessions.');
			} finally {
				sessionPickerLoadingRef.current = false;
				setSessionPickerLoading(false);
			}
		},
		[adapter],
	);

	const openSessionPicker = useCallback(() => {
		setSessionPickerOpen(true);
		setRenamingSessionId(null);
		setRenamingName('');
		setDeleteConfirmSessionId(null);
		setSessions([]);
		setSessionsPageIndex(0);
		setSessionsHasNextPage(false);
		loadSessions(0);
	}, [loadSessions]);

	const loadNextSessions = useCallback(() => {
		if (!sessionsHasNextPage || sessionPickerLoadingRef.current) return;
		loadSessions(sessionsPageIndex + 1);
	}, [loadSessions, sessionsHasNextPage, sessionsPageIndex]);

	const handleSessionPickerScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const target = event.currentTarget;
			const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
			if (distanceFromBottom <= SESSION_SCROLL_THRESHOLD_PX) {
				loadNextSessions();
			}
		},
		[loadNextSessions],
	);

	useEffect(() => {
		if (!adapter.isAvailable) {
			setSessionLoading(false);
			return;
		}

		let cancelled = false;
		const initialize = async () => {
			++sessionResolutionRequestRef.current;
			resetSessionTracking();
			setSending(false);
			setIsPolling(false);
			let defaultAgentId: string | undefined;
			let hasExistingSession = false;
			setAgentOptions([]);
			setSelectedAgentId(undefined);
			setSessionAgentName(undefined);
			if (adapter.getAgents) {
				try {
					const agents = await adapter.getAgents();
					if (cancelled) return;
					setAgentOptions(agents);
					defaultAgentId = getPreferredNewChatAgentId(agents);
					setSelectedAgentId(defaultAgentId);
				} catch (err) {
					console.error('Failed to load chat agents:', err);
				}
			}

			if (adapter.listSessions) {
				try {
					const result = await adapter.listSessions(0, 1);
					if (cancelled) return;
					hasExistingSession = result.items.length > 0;
				} catch (err) {
					console.error('Failed to determine whether a chat session already exists:', err);
				}
			}

			if (!cancelled) {
				// A new chat explicitly selects Domain Modeller. For an existing chat, leave
				// the agent unconstrained so the most recent session is restored as-is.
				await resolveSession(false, hasExistingSession ? undefined : defaultAgentId);
			}
		};

		void initialize();
		return () => {
			cancelled = true;
		};
		// Session initialization is intentionally tied to the stable conversation scope,
		// not to callback identity changes caused by live designer state updates.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [adapter.isAvailable, adapterSessionKey]);

	useEffect(() => {
		if (!shareDialogOpen) {
			return;
		}

		let cancelled = false;
		const loadShareUsers = async () => {
			setShareUsersLoading(true);
			setShareError(null);
			try {
				const name = shareUserSearchText.trim() || undefined;
				const page = await userClient.getUsers(0, SHARE_USER_SEARCH_PAGE_SIZE, name);
				if (!cancelled) {
					setShareUsers([...page.items].sort(compareTenantUsers));
				}
			} catch (error) {
				console.error('Failed to load users for conversation sharing:', error);
				if (!cancelled) {
					setShareError('Failed to load tenant users.');
				}
			} finally {
				if (!cancelled) {
					setShareUsersLoading(false);
				}
			}
		};

		const timer = window.setTimeout(() => {
			void loadShareUsers();
		}, SHARE_USER_SEARCH_DEBOUNCE_MS);

		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [shareDialogOpen, shareUserSearchText, userClient]);

	useEffect(() => {
		const sessionId = currentSessionIdRef.current;
		const getParticipants = adapter.getParticipants;
		if (!shareDialogOpen || !getParticipants || !sessionId) {
			return;
		}

		let cancelled = false;
		const loadSharedParticipants = async () => {
			setSharedParticipantsLoading(true);
			try {
				const participants = await getParticipants(sessionId);
				if (!cancelled) {
					setSharedParticipants(participants.filter((participant) => !participant.isOwner));
				}
			} catch (error) {
				console.error('Failed to load conversation participants:', error);
			} finally {
				if (!cancelled) {
					setSharedParticipantsLoading(false);
				}
			}
		};

		void loadSharedParticipants();
		return () => {
			cancelled = true;
		};
	}, [adapter.getParticipants, currentSessionIdRef, shareDialogOpen]);

	// @mention input handling
	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setInput(value);
		const cursor = e.target.selectionStart ?? value.length;
		const beforeCursor = value.substring(0, cursor);
		const mentionMatch = beforeCursor.match(/@(\w*)$/);
		if (mentionMatch) {
			setMentionQuery(mentionMatch[1]);
		} else {
			setMentionQuery(null);
			setMentionSuggestions([]);
		}
	}, []);

	// Debounced mention search
	useEffect(() => {
		if (mentionQuery === null) {
			setMentionSuggestions([]);
			return;
		}
		if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current);
		mentionTimerRef.current = setTimeout(async () => {
			try {
				const results = await adapter.searchMentions(mentionQuery);
				setMentionSuggestions(results ?? []);
			} catch {
				setMentionSuggestions([]);
			}
		}, 300);
		return () => {
			if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current);
		};
	}, [mentionQuery, adapter]);

	const insertMention = useCallback(
		(suggestion: ChatMentionSuggestion) => {
			const el = inputRef.current;
			const cursor = el?.selectionStart ?? input.length;
			const beforeCursor = input.substring(0, cursor);
			const afterCursor = input.substring(cursor);
			const mentionStart = beforeCursor.lastIndexOf('@');
			const mention = `@${suggestion.type}/${suggestion.name}`;
			const newValue = beforeCursor.substring(0, mentionStart) + mention + ' ' + afterCursor;
			setInput(newValue);
			setMentionQuery(null);
			setMentionSuggestions([]);
			el?.focus();
		},
		[input],
	);

	const handleSelectSession = useCallback(
		async (session: ChatSessionSummary) => {
			if (renamingSessionId || deletingSessionId) return;
			const selectionRequest = ++sessionResolutionRequestRef.current;

			setPendingAttachments((current) => {
				for (const pending of current) {
					URL.revokeObjectURL(pending.previewUrl);
					pendingPreviewUrlsRef.current.delete(pending.previewUrl);
					if (pending.attachment && adapter.deleteDraftAttachment) {
						void adapter.deleteDraftAttachment(pending.attachment.fileId).catch((error) =>
							console.error('Failed to delete chat attachment draft:', error),
						);
					}
				}
				return [];
			});
			setAttachmentPreview(null);
			stopWaiting();
			setSessionLoading(true);
			setErrorBanner(null);
			setSessionPickerError(null);
			try {
				const delta = await adapter.getMessagesDelta(session.sessionId);
				if (selectionRequest !== sessionResolutionRequestRef.current) return;
				const msgs = delta.messages ?? [];
				setHasMoreMessages(delta.hasMoreMessages ?? false);
				setOldestMessageSequence(delta.oldestMessageSequence);
				setCurrentSession(session.sessionId);
				setSessionCreatedDate(session.createdDate);
				setSessionName(session.name);
				setSessionAgentName(session.agentName);
				setSelectedAgentId(session.agentId);
				sessionNameManuallyChangedRef.current = false;
				setSessionIsNew(false);
				isNearMessageBottomRef.current = true;
				setShowJumpToLatest(false);
				setMessages(msgs);
				cacheSessionMessages(session.sessionId, msgs);
				setLastMessageFromMessages(msgs, delta);
				await notifyCompletedAssistantMessages(msgs);
				setSessionPickerOpen(false);
			} catch (err) {
				console.error('Failed to switch chat session:', err);
				setSessionPickerError('Failed to open the selected session.');
			} finally {
				setSessionLoading(false);
			}
		},
		[
			adapter,
			cacheSessionMessages,
			deletingSessionId,
			notifyCompletedAssistantMessages,
			renamingSessionId,
			setCurrentSession,
			setLastMessageFromMessages,
			stopWaiting,
		],
	);

	const startRenameSession = useCallback((session: ChatSessionSummary) => {
		setSessionPickerError(null);
		setDeleteConfirmSessionId(null);
		setRenamingSessionId(session.sessionId);
		setRenamingName(session.name);
	}, []);

	const cancelRenameSession = useCallback(() => {
		setRenamingSessionId(null);
		setRenamingName('');
	}, []);

	const startCurrentSessionRename = useCallback(() => {
		if (!adapter.renameSession || !currentSessionIdRef.current) return;

		setSessionPickerError(null);
		setRenamingSessionId(null);
		setRenamingName('');
		setDeleteConfirmSessionId(null);
		setHeaderRenameName(sessionName ?? '');
		setHeaderRenaming(true);
	}, [adapter, currentSessionIdRef, sessionName]);

	const openShareDialog = useCallback(() => {
		if (!adapter.addParticipants || !currentSessionIdRef.current) {
			return;
		}

		setSelectedShareUsers([]);
		setShareUserSearchText('');
		setShareUsers([]);
		setSharedParticipants([]);
		setSharedParticipantsLoading(false);
		setShareError(null);
		setShareDialogOpen(true);
	}, [adapter.addParticipants, currentSessionIdRef]);

	const shareCurrentSession = useCallback(async () => {
		const sessionId = currentSessionIdRef.current;
		if (!adapter.addParticipants || !sessionId || selectedShareUsers.length === 0 || shareSaving) {
			return;
		}

		setShareSaving(true);
		setShareError(null);
		try {
			await adapter.addParticipants(
				sessionId,
				selectedShareUsers.map((user) => user.id),
			);
			const participants = await adapter.getParticipants?.(sessionId);
			if (participants) {
				setSharedParticipants(participants.filter((participant) => !participant.isOwner));
			}
			setShareDialogOpen(false);
			setSelectedShareUsers([]);
		} catch (error) {
			console.error('Failed to share conversation:', error);
			setShareError('Failed to share the conversation.');
		} finally {
			setShareSaving(false);
		}
	}, [adapter, currentSessionIdRef, selectedShareUsers, shareSaving]);

	const removeSharedParticipant = useCallback(async (participant: ChatParticipant) => {
		const sessionId = currentSessionIdRef.current;
		if (!adapter.removeParticipant || !sessionId || !participant.canRemove || removingParticipantId) {
			return;
		}

		setRemovingParticipantId(participant.userId);
		setShareError(null);
		try {
			await adapter.removeParticipant(sessionId, participant.userId);
			setSharedParticipants((participants) => participants.filter((current) => current.userId !== participant.userId));
		} catch (error) {
			console.error('Failed to remove conversation participant:', error);
			setShareError('Failed to remove access.');
		} finally {
			setRemovingParticipantId(null);
		}
	}, [adapter, currentSessionIdRef, removingParticipantId]);

	const cancelCurrentSessionRename = useCallback(() => {
		setHeaderRenaming(false);
		setHeaderRenameName('');
	}, []);

	const saveCurrentSessionRename = useCallback(async () => {
		const sessionId = currentSessionIdRef.current;
		if (!adapter.renameSession || !sessionId || headerRenameSaving) return;

		const nextName = headerRenameName.trim();
		if (!nextName) {
			setErrorBanner('Conversation name cannot be empty.');
			return;
		}

		setHeaderRenameSaving(true);
		setErrorBanner(null);
		try {
			await adapter.renameSession(sessionId, nextName);
			setSessionName(nextName);
			sessionNameManuallyChangedRef.current = true;
			setSessions((prev) => prev.map((item) => (item.sessionId === sessionId ? { ...item, name: nextName } : item)));
			setHeaderRenaming(false);
			setHeaderRenameName('');
		} catch (err) {
			console.error('Failed to rename current chat session:', err);
			setErrorBanner('Failed to rename the conversation.');
		} finally {
			setHeaderRenameSaving(false);
		}
	}, [adapter, currentSessionIdRef, headerRenameName, headerRenameSaving]);

	const saveRenameSession = useCallback(
		async (session: ChatSessionSummary) => {
			if (!adapter.renameSession || renamingSaving) return;

			const nextName = renamingName.trim();
			if (!nextName) {
				setSessionPickerError('Conversation name cannot be empty.');
				return;
			}

			setRenamingSaving(true);
			setSessionPickerError(null);
			try {
				await adapter.renameSession(session.sessionId, nextName);
				setSessions((prev) => prev.map((item) => (item.sessionId === session.sessionId ? { ...item, name: nextName } : item)));
				if (currentSessionIdRef.current === session.sessionId) {
					setSessionName(nextName);
					sessionNameManuallyChangedRef.current = true;
				}
				setRenamingSessionId(null);
				setRenamingName('');
			} catch (err) {
				console.error('Failed to rename chat session:', err);
				setSessionPickerError('Failed to rename the conversation.');
			} finally {
				setRenamingSaving(false);
			}
		},
		[adapter, currentSessionIdRef, renamingName, renamingSaving],
	);

	const startDeleteSession = useCallback((session: ChatSessionSummary) => {
		setSessionPickerError(null);
		setRenamingSessionId(null);
		setRenamingName('');
		setDeleteConfirmSessionId(session.sessionId);
	}, []);

	const cancelDeleteSession = useCallback(() => {
		setDeleteConfirmSessionId(null);
	}, []);

	const confirmDeleteSession = useCallback(
		async (session: ChatSessionSummary) => {
			if (!adapter.deleteSession || deletingSessionId) return;

			const isCurrent = currentSessionIdRef.current === session.sessionId;
			setDeletingSessionId(session.sessionId);
			setSessionPickerError(null);
			try {
				await adapter.deleteSession(session.sessionId);
				setSessions((prev) => prev.filter((item) => item.sessionId !== session.sessionId));
				setDeleteConfirmSessionId(null);

				if (isCurrent) {
					stopWaiting();
					resetSessionTracking();
					setMessages([]);
					setHasMoreMessages(false);
					setOldestMessageSequence(undefined);
					setSessionCreatedDate(null);
					setSessionName(null);
					sessionNameManuallyChangedRef.current = false;
					setSessionIsNew(true);
					setSessionPickerOpen(false);
					await resolveSession(false, selectedAgentId);
				}
			} catch (err) {
				console.error('Failed to delete chat session:', err);
				setSessionPickerError('Failed to delete the conversation.');
			} finally {
				setDeletingSessionId(null);
			}
		},
		[adapter, currentSessionIdRef, deletingSessionId, resetSessionTracking, resolveSession, selectedAgentId, stopWaiting],
	);

	const refreshCurrentSession = useCallback(async () => {
		const sessionId = currentSessionIdRef.current;
		if (!adapter.isAvailable || !sessionId || refreshingSession) {
			return;
		}

		setRefreshingSession(true);
		setErrorBanner(null);
		try {
			const delta = await adapter.getMessagesDelta(sessionId);
			if (currentSessionIdRef.current !== sessionId) return;
			const msgs = delta.messages ?? [];
			setMessages(msgs);
			setHasMoreMessages(delta.hasMoreMessages ?? false);
			setOldestMessageSequence(delta.oldestMessageSequence);
			setLastMessageFromMessages(msgs, delta);

			if (delta.status === 'failed') {
				const failText = msgs.find((m) => m.role === 'assistant')?.text ?? 'An error occurred processing your request.';
				setErrorBanner(failText);
				stopWaiting();
			} else if (delta.isComplete) {
				stopWaiting();
			}
		} catch (err) {
			console.error('Failed to refresh chat session:', err);
			setErrorBanner('Failed to refresh the conversation.');
		} finally {
			setRefreshingSession(false);
		}
	}, [adapter, currentSessionIdRef, refreshingSession, setLastMessageFromMessages, stopWaiting]);

	const loadCurrentSessionDelta = useCallback(() => {
		const sessionId = currentSessionIdRef.current;
		if (sessionId) {
			return loadMessagesDelta(sessionId);
		}
	}, [currentSessionIdRef, loadMessagesDelta]);

	useNotificationRefetch<ConversationEventNotification>({
		notification: latestConversationEvent,
		getKey: useCallback((event) => `${event.payload.id}:${event.payload.messageId ?? event.receivedAt}`, []),
		shouldRefetch: useCallback((event) => event.payload.id === currentSessionIdRef.current, [currentSessionIdRef]),
		refetch: loadCurrentSessionDelta,
	});

	useEffect(() => {
		const conversationEvent = latestConversationEvent;
		const conversationStatus = conversationEvent?.payload.status?.trim().toLowerCase();
		if (
			conversationEvent?.payload.id === currentSessionIdRef.current &&
			conversationStatus &&
			TERMINAL_CONVERSATION_STATUSES.has(conversationStatus) &&
			responseStatus !== CHAT_RESPONSE.queued && responseStatus !== CHAT_RESPONSE.responding && responseStatus !== CHAT_RESPONSE.stopping
		) {
			stopWaiting();
		}
	}, [currentSessionIdRef, latestConversationEvent, responseStatus, stopWaiting]);

	useEffect(() => {
		const sessionId = currentSessionIdRef.current;
		const wasConnected = previousConnectionStateRef.current === signalR.HubConnectionState.Connected;
		previousConnectionStateRef.current = connectionState;

		if (wasConnected || !sessionId || connectionState !== signalR.HubConnectionState.Connected) {
			return;
		}

		void loadMessagesDelta(sessionId);
	}, [connectionState, currentSessionIdRef, loadMessagesDelta]);

	// Cleanup on unmount
	useEffect(
		() => () => {
			stopWaiting();
			if (bottomScrollAnimationFrameRef.current !== undefined) {
				cancelAnimationFrame(bottomScrollAnimationFrameRef.current);
			}
			pendingPreviewUrlsRef.current.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
			pendingPreviewUrlsRef.current.clear();
		},
		[stopWaiting],
	);

	const scrollToLatest = useCallback((behavior: ScrollBehavior = 'smooth') => {
		isNearMessageBottomRef.current = true;
		setShowJumpToLatest(false);
		if (bottomScrollAnimationFrameRef.current !== undefined) {
			cancelAnimationFrame(bottomScrollAnimationFrameRef.current);
		}

		let frame = 0;
		const settleAtBottom = () => {
			const container = messagesContainerRef.current;
			if (!container) {
				return;
			}

			container.scrollTo({ top: container.scrollHeight, behavior: frame === 0 ? behavior : 'auto' });
			frame += 1;
			if (frame < BOTTOM_SCROLL_SETTLE_FRAMES) {
				bottomScrollAnimationFrameRef.current = requestAnimationFrame(settleAtBottom);
			} else {
				bottomScrollAnimationFrameRef.current = undefined;
			}
		};

		settleAtBottom();
	}, []);

	const loadEarlierMessages = useCallback(async () => {
		const sessionId = currentSessionIdRef.current;
		if (
			!adapter.getMessagesBefore ||
			!sessionId ||
			!hasMoreMessages ||
			oldestMessageSequence === undefined ||
			loadingEarlierMessagesRef.current
		) {
			return;
		}

		loadingEarlierMessagesRef.current = true;
		setLoadingEarlierMessages(true);
		const container = messagesContainerRef.current;
		const previousScrollHeight = container?.scrollHeight ?? 0;
		const previousScrollTop = container?.scrollTop ?? 0;
		try {
			const page = await adapter.getMessagesBefore(sessionId, oldestMessageSequence, MESSAGE_PAGE_SIZE);
			if (sessionId !== currentSessionIdRef.current) {
				return;
			}

			setMessages((current) => mergeChatMessages(page.messages, current));
			setHasMoreMessages(page.hasMoreMessages);
			setOldestMessageSequence(page.oldestMessageSequence);
			requestAnimationFrame(() => {
				const updatedContainer = messagesContainerRef.current;
				if (updatedContainer && sessionId === currentSessionIdRef.current) {
					updatedContainer.scrollTop = previousScrollTop + updatedContainer.scrollHeight - previousScrollHeight;
				}
			});
		} catch (err) {
			console.error('Failed to load earlier chat messages:', err);
		} finally {
			loadingEarlierMessagesRef.current = false;
			setLoadingEarlierMessages(false);
		}
	}, [adapter, currentSessionIdRef, hasMoreMessages, oldestMessageSequence]);

	const handleMessagesScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
		const isNearBottom = scrollHeight - scrollTop - clientHeight < 56;
		isNearMessageBottomRef.current = isNearBottom;
		setShowJumpToLatest(!isNearBottom);
		if (scrollTop <= MESSAGE_SCROLL_TOP_THRESHOLD_PX) {
			void loadEarlierMessages();
		}
	}, [loadEarlierMessages]);

	// Tool progress is rendered as a compact group. A full persisted page can therefore
	// fit without creating a scrollbar, in which case onScroll can never request history.
	// Prefetch a single older page on first display; later pages remain user initiated so
	// live progress updates do not repeatedly redraw the conversation.
	useEffect(() => {
		const container = messagesContainerRef.current;
		const sessionId = currentSessionIdRef.current;
		if (
			adapter.getMessagesBefore &&
			sessionId &&
			autoPrefetchedHistorySessionIdRef.current !== sessionId &&
			hasMoreMessages &&
			!loadingEarlierMessages &&
			container &&
			container.scrollHeight <= container.clientHeight
		) {
			autoPrefetchedHistorySessionIdRef.current = sessionId;
			void loadEarlierMessages();
		}
	}, [adapter.getMessagesBefore, currentSessionIdRef, hasMoreMessages, loadingEarlierMessages, loadEarlierMessages, messages]);

	// Preserve the reader's place when they scroll back through a long conversation.
	// Restored messages are set while the loading spinner is visible, so rerun when it
	// disappears and the bottom anchor has been mounted.
	useEffect(() => {
		if (isNearMessageBottomRef.current) {
			scrollToLatest('auto');
		}
	}, [messages, scrollToLatest, sessionLoading]);

	useEffect(() => {
		cacheSessionMessages(currentSessionIdRef.current, messages);
	}, [cacheSessionMessages, currentSessionIdRef, messages]);

	const {
		isSupported: supportsSpeechRecognition,
		isListening,
		toggle: toggleSpeechRecognition,
		stop: stopSpeechRecognition,
	} = useSpeechRecognition({
		value: input,
		onChange: setInput,
		onError: setErrorBanner,
		language: speechLanguage,
	});

	const buildInitialSessionName = useCallback((prompt: string) => prompt.substring(0, INITIAL_SESSION_NAME_MAX_LENGTH), []);

	const uploadPendingAttachment = useCallback(
		async (pending: PendingAttachment) => {
			const conversationId = currentSessionIdRef.current;
			if (!adapter.uploadAttachment || !conversationId) return;

			setPendingAttachments((current) =>
				current.map((item) => (item.localId === pending.localId ? { ...item, status: 'uploading', error: undefined } : item)),
			);
			try {
				const attachment = await adapter.uploadAttachment(pending.file, conversationId);
				setPendingAttachments((current) =>
					current.map((item) => (item.localId === pending.localId ? { ...item, attachment, status: 'uploaded', error: undefined } : item)),
				);
			} catch (error) {
				console.error('Failed to upload chat attachment:', error);
				setPendingAttachments((current) =>
					current.map((item) =>
						item.localId === pending.localId ? { ...item, status: 'failed', error: 'Upload failed' } : item,
					),
				);
			}
		},
		[adapter, currentSessionIdRef],
	);

	const addAttachmentFiles = useCallback(
		(files: File[]) => {
			if (!adapter.uploadAttachment || files.length === 0) return;
			if (pendingAttachments.length + files.length > MAX_ATTACHMENT_COUNT) {
				setErrorBanner(`A message can contain at most ${MAX_ATTACHMENT_COUNT} attachments.`);
				return;
			}

			const invalidFile = files.find(
				(file) => file.size <= 0 || file.size > MAX_ATTACHMENT_BYTES || !SUPPORTED_ATTACHMENT_TYPES.has(getAttachmentContentType(file)),
			);
			if (invalidFile) {
				setErrorBanner(`“${invalidFile.name}” is empty, too large, or has an unsupported file type.`);
				return;
			}

			const totalBytes = pendingAttachments.reduce((total, item) => total + item.file.size, 0) + files.reduce((total, file) => total + file.size, 0);
			if (totalBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
				setErrorBanner(`Attachments can total at most ${MAX_TOTAL_ATTACHMENT_BYTES / 1024 / 1024} MB per message.`);
				return;
			}

			setErrorBanner(null);
			const additions = files.map<PendingAttachment>((file) => {
				const previewUrl = URL.createObjectURL(file);
				pendingPreviewUrlsRef.current.add(previewUrl);
				return {
					localId: uuidv4(),
					file,
					previewUrl,
					status: 'uploading',
				};
			});
			setPendingAttachments((current) => [...current, ...additions]);
			for (const pending of additions) {
				void uploadPendingAttachment(pending);
			}
		},
		[adapter.uploadAttachment, pendingAttachments, uploadPendingAttachment],
	);

	const removePendingAttachment = useCallback(
		(pending: PendingAttachment) => {
			URL.revokeObjectURL(pending.previewUrl);
			pendingPreviewUrlsRef.current.delete(pending.previewUrl);
			setAttachmentPreview(null);
			setPendingAttachments((current) => current.filter((item) => item.localId !== pending.localId));
			if (pending.attachment && adapter.deleteDraftAttachment) {
				void adapter.deleteDraftAttachment(pending.attachment.fileId).catch((error) =>
					console.error('Failed to delete chat attachment draft:', error),
				);
			}
		},
		[adapter],
	);

	const discardPendingAttachments = useCallback(() => {
		for (const pending of pendingAttachments) {
			URL.revokeObjectURL(pending.previewUrl);
			pendingPreviewUrlsRef.current.delete(pending.previewUrl);
			if (pending.attachment && adapter.deleteDraftAttachment) {
				void adapter.deleteDraftAttachment(pending.attachment.fileId).catch((error) =>
					console.error('Failed to delete chat attachment draft:', error),
				);
			}
		}
		setAttachmentPreview(null);
		setPendingAttachments([]);
	}, [adapter, pendingAttachments]);

	const resolveAttachmentUrl = useCallback(
		async (attachment: ChatAttachment) => {
			const scope = attachmentAdapterRef.current;
			const sessionId = currentSessionIdRef.current;
			if (scope.key !== adapterSessionKey || !sessionId || !scope.adapter.getAttachmentDownloadUrl) {
				throw new Error('Attachment previews are unavailable.');
			}

			const cacheKey = `${sessionId}:${attachment.fileId}`;
			const cached = attachmentUrlCacheRef.current.get(cacheKey);
			if (cached && Date.now() - cached.resolvedAt < ATTACHMENT_URL_CACHE_DURATION_MS) return cached.url;

			const url = await scope.adapter.getAttachmentDownloadUrl(sessionId, attachment.fileId);
			if (attachmentAdapterRef.current.key === adapterSessionKey && currentSessionIdRef.current === sessionId) {
				attachmentUrlCacheRef.current.set(cacheKey, { url, resolvedAt: Date.now() });
			}
			return url;
		},
		[adapterSessionKey, currentSessionIdRef],
	);

	const previewAttachment = useCallback((attachment: ChatAttachment, attachments: ChatAttachment[]) => {
		setAttachmentPreview({
			selection: { attachment, attachments },
			resolveUrl: resolveAttachmentUrl,
		});
	}, [resolveAttachmentUrl]);

	const resolveHtml = useCallback(async (fileId: string, signal?: AbortSignal) => {
		const scope = attachmentAdapterRef.current;
		const adapter = scope.adapter;
		const sessionId = currentSessionIdRef.current;
		console.info('[PromptChat HTML trace] resolver invoked', {
			sessionKey: adapter.sessionKey,
			hasSessionId: Boolean(sessionId),
			hasAdapterResolver: Boolean(adapter.getAttachmentHtml),
			fileId,
		});
		if (scope.key !== adapterSessionKey || !sessionId || !adapter.getAttachmentHtml) {
			console.warn('[PromptChat HTML trace] resolver cannot fetch attachment', {
				sessionKey: adapter.sessionKey,
				hasSessionId: Boolean(sessionId),
				hasAdapterResolver: Boolean(adapter.getAttachmentHtml),
				fileId,
			});
			throw new Error('HTML previews are unavailable.');
		}
		const html = await adapter.getAttachmentHtml(sessionId, fileId, signal);
		console.info('[PromptChat HTML trace] attachment fetched', { sessionKey: adapter.sessionKey, fileId, characterCount: html.length });
		return html;
	}, [adapterSessionKey, currentSessionIdRef]);
	useEffect(() => {
		console.info('[PromptChat HTML trace] adapter capabilities', {
			sessionKey: adapter.sessionKey,
			hasHtmlResolver: Boolean(adapter.getAttachmentHtml),
			hasDownloadResolver: Boolean(adapter.getAttachmentDownloadUrl),
		});
	}, [adapter]);

	const previewPendingAttachment = useCallback((pending: PendingAttachment) => {
		const attachments = pendingAttachments.map<ChatAttachment>((item) => ({
			fileId: item.localId,
			fileName: item.file.name,
			contentType: getAttachmentContentType(item.file),
			size: item.file.size,
		}));
		const previewUrls = new Map(pendingAttachments.map((item) => [item.localId, item.previewUrl]));
		const attachment = attachments.find((item) => item.fileId === pending.localId);
		if (!attachment) return;

		setAttachmentPreview({
			selection: { attachment, attachments },
			resolveUrl: (item) => {
				const previewUrl = previewUrls.get(item.fileId);
				return previewUrl ? Promise.resolve(previewUrl) : Promise.reject(new Error('The local attachment is unavailable.'));
			},
		});
	}, [pendingAttachments]);

	const startNewSession = useCallback(() => {
		discardPendingAttachments();
		stopSpeechRecognition();
		if (agentOptions.length <= 1) {
			void resolveSession(true, selectedAgentId);
			return;
		}

		++sessionResolutionRequestRef.current;
		stopWaiting();
		resetSessionTracking();
		setMessages([]);
		setHasMoreMessages(false);
		setOldestMessageSequence(undefined);
		setSessionCreatedDate(null);
		setSessionName(null);
		setSessionIsNew(true);
		setErrorBanner(null);
		setInput('');
		setSelectedAgentId(getPreferredNewChatAgentId(agentOptions));
		setSessionAgentName(undefined);
		sessionNameManuallyChangedRef.current = false;
	}, [agentOptions, discardPendingAttachments, resetSessionTracking, resolveSession, selectedAgentId, stopSpeechRecognition, stopWaiting]);

	const handleSend = useCallback(async () => {
		const prompt = input.trim();
		const uploadedAttachments = pendingAttachments
			.map((pending) => pending.attachment)
			.filter((attachment): attachment is ChatAttachment => !!attachment);
		if ((!prompt && uploadedAttachments.length === 0) || pendingAttachments.some((pending) => pending.status !== 'uploaded') || sending || isPolling) return;
		stopSpeechRecognition();

		const isFirstInstruction = sessionIsNew && messages.length === 0 && !sessionNameManuallyChangedRef.current;
		const conversationName = isFirstInstruction
			? buildInitialSessionName(prompt || uploadedAttachments[0]?.fileName || 'Attachment')
			: undefined;
		const previousSessionName = sessionName;

		setInput('');
		setMentionQuery(null);
		setMentionSuggestions([]);
		setErrorBanner(null);
		isNearMessageBottomRef.current = true;
		setShowJumpToLatest(false);
		if (conversationName) {
			setSessionName(conversationName);
		}

		setSending(true);
		// Start waiting before dispatching. A very fast agent can publish its final message
		// before instruct resolves; starting afterwards would overwrite stopWaiting from SignalR.
		setIsPolling(true);
		const sendEpoch = sessionResolutionRequestRef.current;
		try {
			const forceNewSession = !currentSessionIdRef.current;
			const retryKey = JSON.stringify([adapter.sessionKey, currentSessionIdRef.current, prompt, uploadedAttachments.map((attachment) => attachment.fileId), chatMode, currentChangeSetId, area, selectedAgentId]);
			if (retrySendRef.current?.key !== retryKey) retrySendRef.current = { key: retryKey, requestId: uuidv4() };
			const response = await adapter.instruct(
				prompt,
				currentSessionIdRef.current,
				conversationName,
				forceNewSession,
				selectedAgentId,
				{
					...(area ? { Area: area } : {}),
					...(currentChangeSetId ? { ChangeSetId: currentChangeSetId } : {}),
					Mode: chatMode,
				},
				uploadedAttachments.map((attachment) => attachment.fileId),
				forceNewSession || !adapter.retryableSends ? undefined : retrySendRef.current.requestId,
			);
			if (sendEpoch !== sessionResolutionRequestRef.current) return;
			retrySendRef.current = undefined;
			if (response.warning) setErrorBanner(response.warning);
			for (const pending of pendingAttachments) {
				URL.revokeObjectURL(pending.previewUrl);
				pendingPreviewUrlsRef.current.delete(pending.previewUrl);
			}
			setAttachmentPreview(null);
			setPendingAttachments([]);
			setCurrentSession(response.sessionId);
			setSessionIsNew(false);
			await loadMessagesDelta(response.sessionId);
		} catch (err) {
			if (sendEpoch !== sessionResolutionRequestRef.current) return;
			const status = (err as { status?: number })?.status;
			if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) retrySendRef.current = undefined;
			console.error('Failed to send instruction:', err);
			if (conversationName) {
				setSessionName(previousSessionName);
			}
			setInput(prompt);
			setErrorBanner(!adapter.retryableSends && (!status || status >= 500)
				? 'Instruction delivery could not be confirmed. Refresh this chat before retrying to avoid running it twice.'
				: 'Failed to send instruction. Please retry the original message.');
			stopWaiting();
		} finally {
			if (sendEpoch === sessionResolutionRequestRef.current) setSending(false);
		}
	}, [
		adapter,
		area,
		buildInitialSessionName,
		chatMode,
		currentChangeSetId,
		currentSessionIdRef,
		input,
		isPolling,
		loadMessagesDelta,
		messages.length,
		pendingAttachments,
		sending,
		sessionIsNew,
		sessionName,
		selectedAgentId,
		setCurrentSession,
		stopSpeechRecognition,
		stopWaiting,
	]);

	const sessionDate = sessionCreatedDate ? formatTimestampFull(sessionCreatedDate) : '';
	const startNewSessionTitle = currentSessionIdRef.current ? `Start new session (current: ${sessionDate}, ${messages.length} messages)` : 'Start new session';
	const displayItems = React.useMemo(() => buildChatDisplayItems(messages), [messages]);
	const isAgentResponding = isPolling && (responseStatus === CHAT_RESPONSE.queued || responseStatus === CHAT_RESPONSE.responding);
	useEffect(() => {
		onStatusChange?.({ busy: sending || isPolling, failed: !!errorBanner || responseStatus === CHAT_RESPONSE.failed || responseStatus === CHAT_RESPONSE.stalled, sessionId: currentSessionId });
	}, [sending, isPolling, errorBanner, responseStatus, currentSessionId, onStatusChange]);
	const respondingProgressGroupIndex = isAgentResponding && displayItems[displayItems.length - 1]?.kind === 'progressGroup' ? displayItems.length - 1 : -1;
	const canChangeSession = adapter.isAvailable && !!adapter.listSessions && !sending && !isPolling;
	const deleteConfirmSession = sessions.find((session) => session.sessionId === deleteConfirmSessionId);
	const selectedAgent = agentOptions.find((agent) => agent.id === selectedAgentId);
	const headerTitle = showAgentNameInHeader
		? selectedAgent?.name?.trim() || sessionAgentName?.trim() || defaultTitle
		: showConversationNameBar ? defaultTitle : sessionName?.trim() || defaultTitle;
	const hasConversationNameBar = showConversationNameBar || showAgentNameInHeader || chatDialogOpen;
	const hasAutomaticContext = !!adapter.getContextAttributes && composerContext == null;
	const contextAttributes = Object.fromEntries(Object.entries({
		...adapter.getContextAttributes?.(),
		...(area ? { Area: area } : {}),
		...(currentChangeSetId ? { ChangeSetId: currentChangeSetId } : {}),
	}).filter(([key, value]) => key !== 'Mode' && !!value));
	const context = usePromptChatContext({ attributes: contextAttributes });
	const avatarAgentId = selectedAgentId ?? adapter.agentId;
	const renderAssistantAvatar = (size: number, headerPortrait = false) => {
		const fallback = headerIcon ?? <Avatar src="/assets/octopus-compact.png" alt="Randol" sx={{ ...randolAvatarSx, width: size, height: size }} />;
		return avatarAgentId ? <ChatAgentAvatar agentId={avatarAgentId} size={headerPortrait ? 38 : size} headerPortrait={headerPortrait} fallback={fallback} /> : fallback;
	};
	const canRenameCurrentSession = adapter.isAvailable && !!adapter.renameSession && !!currentSessionIdRef.current && !sessionLoading;
	const canRefreshCurrentSession = adapter.isAvailable && !!currentSessionIdRef.current && !sessionLoading && !refreshingSession;
	const canStartNewSession = adapter.isAvailable && !sessionLoading && !sending && !isPolling;
	const hasCurrentSession = !!currentSessionIdRef.current && !sessionLoading;
	const hasHeaderOverflowActions = hasCurrentSession;
	const canSelectAgent = agentOptions.length > 1 && messages.length === 0 && !sending && !isPolling && !sessionLoading;
	const handleAgentSelection = useCallback(
		(agentId: string | null) => {
			if (!agentId || !canSelectAgent || agentId === selectedAgentId) {
				return;
			}

			if (sessionIsNew) {
				setSelectedAgentId(agentId);
				setSessionAgentName(undefined);
				return;
			}

			// A resumed empty session is already assigned to an agent. Selecting a different
			// agent starts a clean session so the next prompt is sent to the intended agent.
			void resolveSession(true, agentId);
		},
		[canSelectAgent, resolveSession, selectedAgentId, sessionIsNew],
	);
	const openChatDialog = useCallback(() => {
		setChatDialogOpen(true);
		onDialogOpenChange?.(true);
	}, [onDialogOpenChange]);
	const closeChatDialog = useCallback(() => {
		setChatDialogOpen(false);
		onDialogOpenChange?.(false);
	}, [onDialogOpenChange]);
	const closeHeaderMenu = useCallback(() => setHeaderMenuAnchorEl(null), []);

	const content = (
		<>
			{/* Not-yet-available notice */}
			{!adapter.isAvailable && (
				<Alert severity="info" sx={{ m: 1.5, mb: 0 }}>
					{unavailableText ?? 'This chat endpoint is not yet available. It will be connected once the backend is ready.'}
				</Alert>
			)}

			{/* Messages area */}
			<Box
				ref={messagesContainerRef}
				onScroll={handleMessagesScroll}
				sx={{
					px: 1.5,
					py: 1,
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					position: 'relative',
				}}
			>
				{sessionLoading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
						<CircularProgress size={22} />
					</Box>
				) : messages.length === 0 && emptyStateText ? (
					<Box sx={{ display: 'grid', justifyItems: 'center', gap: 1, px: 2, pt: 5, textAlign: 'center' }}>
						{renderAssistantAvatar(44)}
						<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
							Start a conversation
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280 }}>
							{emptyStateText}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							Use @ to include relevant context.
						</Typography>
						{agentOptions.length > 1 && (
							<Box sx={{ display: 'grid', gap: 0.75, justifyItems: 'center', mt: 0.75, width: '100%' }}>
								<ToggleButtonGroup
									exclusive
									size="small"
									value={selectedAgentId ?? null}
									onChange={(_, value: string | null) => handleAgentSelection(value)}
									aria-label="Conversation agent"
								>
									{agentOptions.map((agent) => (
										<ToggleButton key={agent.id} value={agent.id} disabled={!canSelectAgent} sx={{ px: 1.5, textTransform: 'none' }}>
											{agent.name}
										</ToggleButton>
									))}
								</ToggleButtonGroup>
								{selectedAgent?.description && (
									<Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }}>
										{selectedAgent.description}
									</Typography>
								)}
							</Box>
						)}
						{suggestedPrompts.length > 0 && (
							<Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, mt: 0.5 }}>
								{suggestedPrompts.map((prompt) => (
									<Button
										key={prompt}
										size="small"
										variant="outlined"
										onClick={() => {
											setInput(prompt);
											inputRef.current?.focus();
										}}
										sx={{ textTransform: 'none', borderRadius: 4, fontSize: '0.72rem' }}
									>
										{prompt}
									</Button>
								))}
							</Box>
						)}
					</Box>
				) : (
					<>
						{hasMoreMessages && !loadingEarlierMessages && (
							<Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
								<Button size="small" onClick={() => void loadEarlierMessages()} sx={{ textTransform: 'none' }}>
									Load earlier messages
								</Button>
							</Box>
						)}
						{loadingEarlierMessages && (
							<Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
								<CircularProgress size={16} />
							</Box>
						)}
						{displayItems.map((item, idx) => {
							if (item.kind === 'progressGroup') {
								return <MemoizedToolProgressGroup key={`progress-${idx}`} messages={item.messages} partialMessages={item.partialMessages} responding={idx === respondingProgressGroupIndex} resolveHtml={adapter.getAttachmentHtml ? resolveHtml : undefined} resolveAttachmentUrl={adapter.getAttachmentDownloadUrl ? resolveAttachmentUrl : undefined} />;
							}

							return (
								<ChatMessageBubble
									key={item.message.id || String(idx)}
									message={item.message}
									timestampNow={timestampNow}
									onPreviewAttachment={adapter.getAttachmentDownloadUrl ? previewAttachment : undefined}
									resolveAttachmentUrl={adapter.getAttachmentDownloadUrl ? resolveAttachmentUrl : undefined}
									resolveHtml={adapter.getAttachmentHtml ? resolveHtml : undefined}
								/>
							);
						})}

						{isAgentResponding && respondingProgressGroupIndex < 0 && (
							<Typography role="status" variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
								<CircularProgress size={12} /> Agent is responding…
							</Typography>
						)}
						{(stopPending || responseStatus === CHAT_RESPONSE.stopping || responseStatus === CHAT_RESPONSE.canceled) && (
							<Typography role="status" variant="caption" sx={{ display: 'block', py: 1 }}>
								{responseStatus === CHAT_RESPONSE.canceled ? 'Agent stopped. Completed changes have been kept.' : 'Stopping agent…'}
							</Typography>
						)}
						{errorBanner && (
							<Alert severity="error" sx={{ mx: 0, mb: 0.5, py: 0.25, fontSize: '0.75rem' }} onClose={() => setErrorBanner(null)}>
								{errorBanner}
							</Alert>
						)}

					</>
				)}
				{showJumpToLatest && (
					<Tooltip title="Jump to latest message">
						<IconButton
							aria-label="Jump to latest message"
							onClick={() => scrollToLatest()}
							size="small"
							sx={{
								position: 'sticky',
								left: '100%',
								bottom: 8,
								zIndex: 1,
								bgcolor: 'background.paper',
								border: '1px solid',
								borderColor: 'divider',
								boxShadow: 2,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							<ArrowDownwardIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
			</Box>

			{/* Input area */}
			{adapter.isAvailable && (
				<Box sx={{ px: 1.5, pb: 1.5, pt: 1.25, flexShrink: 0, position: 'relative', bgcolor: 'background.paper' }}>
					{/* @mention dropdown */}
					{mentionQuery !== null && (
						<Paper
							elevation={4}
							sx={{
								position: 'absolute',
								bottom: '100%',
								left: 12,
								right: 12,
								zIndex: 10,
								maxHeight: 280,
								display: 'flex',
								flexDirection: 'column',
								mb: 0.5,
							}}
						>
							<List dense disablePadding sx={{ overflowY: 'auto' }}>
								{mentionSuggestions.length === 0 ? (
									<ListItem>
										<ListItemText primary="No results" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
									</ListItem>
								) : (
									mentionSuggestions.map((s, idx) => (
										<ListItem key={idx} onClick={() => insertMention(s)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
											<ListItemText primary={`@${s.type}/${s.name}`} primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace' } }} />
										</ListItem>
									))
								)}
							</List>
						</Paper>
					)}
					<Paper
						variant="outlined"
						role="group"
						aria-label="Message composer"
						onDragOver={(event) => {
							if (adapter.uploadAttachment) event.preventDefault();
						}}
						onDrop={(event) => {
							if (!adapter.uploadAttachment) return;
							event.preventDefault();
							addAttachmentFiles(Array.from(event.dataTransfer.files));
						}}
						sx={(theme) => {
							const modeColor = getChatModeColor(theme, chatMode);

							return {
								borderRadius: 3,
								px: 1.5,
								pt: 1.5,
								pb: 1,
								display: 'flex',
								flexDirection: 'column',
								bgcolor: theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.04) : darken(theme.palette.background.paper, 0.025),
								backgroundImage: 'none',
								borderColor: 'divider',
								transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
									duration: theme.transitions.duration.shortest,
								}),
								'&:focus-within': {
									borderColor: modeColor,
									boxShadow: `0 0 0 2px ${alpha(modeColor, 0.16)}`,
								},
							};
						}}
					>
						<input
							ref={fileInputRef}
							type="file"
							multiple
							accept={CHAT_ATTACHMENT_ACCEPT}
							style={{ display: 'none' }}
							onChange={(event) => {
								addAttachmentFiles(Array.from(event.target.files ?? []));
								event.target.value = '';
							}}
						/>
						{pendingAttachments.length > 0 && (
							<Box sx={{ display: 'grid', gap: 0.5, mb: 0.75 }}>
								{pendingAttachments.map((pending) => (
									<Box
										key={pending.localId}
										sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, px: 0.75, py: 0.35, borderRadius: 1, bgcolor: 'action.hover' }}
									>
										<AttachFileIcon sx={{ fontSize: 16, color: pending.status === 'failed' ? 'error.main' : 'text.secondary' }} />
										<Typography variant="caption" noWrap sx={{ minWidth: 0, flex: 1 }}>
											{pending.file.name} · {formatAttachmentSize(pending.file.size)}
											{pending.error ? ` · ${pending.error}` : ''}
										</Typography>
										{pending.status === 'uploading' && <CircularProgress size={14} />}
										{pending.status === 'failed' && (
											<Tooltip title="Retry upload">
												<IconButton size="small" onClick={() => void uploadPendingAttachment(pending)} disabled={sending || isPolling}>
													<RetryIcon sx={{ fontSize: 16 }} />
												</IconButton>
											</Tooltip>
										)}
										<Tooltip title="Preview attachment">
											<IconButton size="small" onClick={() => previewPendingAttachment(pending)} disabled={sending || isPolling}>
												<PreviewIcon sx={{ fontSize: 16 }} />
											</IconButton>
										</Tooltip>
										<Tooltip title="Remove attachment">
											<IconButton size="small" onClick={() => removePendingAttachment(pending)} disabled={sending || isPolling}>
												<CloseIcon sx={{ fontSize: 16 }} />
											</IconButton>
										</Tooltip>
									</Box>
								))}
							</Box>
						)}
						<TextField
							inputRef={inputRef}
							value={input}
							onChange={handleInputChange}
							onKeyDown={(event) => {
								if (event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey && event.key === 'Enter') {
									event.preventDefault();
									void handleSend();
									return;
								}

								if (event.key === 'Escape') {
									setMentionQuery(null);
									setMentionSuggestions([]);
								}
							}}
							onPaste={(event) => {
								const files = Array.from(event.clipboardData.files);
								if (files.length > 0 && adapter.uploadAttachment) {
									event.preventDefault();
									addAttachmentFiles(files);
								}
							}}
							placeholder="Ask Randol…"
							multiline
							minRows={2}
							maxRows={6}
							fullWidth
							variant="standard"
							disabled={sending || isPolling}
							slotProps={{ input: { disableUnderline: true }, htmlInput: { 'aria-label': 'Message' } }}
							sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', pt: 0 } }}
						/>
						{composerContext}
						{hasAutomaticContext && context.composerContext}
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								columnGap: 1,
								rowGap: 1,
								mt: 1,
							}}
						>
							{adapter.uploadAttachment && (
								<Tooltip title="Attach files">
									<span>
										<IconButton
											aria-label="Attach files"
											sx={composerIconButtonSx}
											onClick={() => fileInputRef.current?.click()}
											disabled={sending || isPolling || pendingAttachments.length >= MAX_ATTACHMENT_COUNT}
											size="small"
										>
											<AttachFileIcon fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>
							)}
							{composerLeadingActions}
							{hasAutomaticContext && context.composerLeadingActions}
								<ToggleButtonGroup
									exclusive
									size="small"
									value={chatMode}
									onChange={(_, value: ChatMode | null) => {
										if (value) {
											setChatMode(value);
										}
									}}
									disabled={sending || isPolling}
									aria-label="Chat mode"
									sx={(theme) => ({
										flexShrink: 0,
										p: 0.25,
										border: '1px solid',
										borderColor: 'divider',
										borderRadius: 1.5,
										bgcolor: alpha(theme.palette.text.primary, 0.025),
										'& .MuiToggleButton-root': {
											gap: 0.5,
											height: 26,
											px: 0.9,
											py: 0,
											border: 0,
											borderRadius: 1.1,
											color: 'text.secondary',
											fontSize: '0.67rem',
											fontWeight: 600,
											lineHeight: 1.4,
											textTransform: 'none',
											transition: theme.transitions.create(['background-color', 'color', 'box-shadow', 'transform'], {
												duration: theme.transitions.duration.shortest,
											}),
											'&:hover': {
												bgcolor: 'action.hover',
												color: 'text.primary',
											},
											'&.Mui-selected': {
												bgcolor: 'primary.main',
												color: 'primary.contrastText',
												boxShadow: `0 2px 5px ${alpha(theme.palette.primary.main, 0.28)}`,
												'&:hover': {
													bgcolor: 'primary.dark',
													color: 'primary.contrastText',
												},
											},
											'&[value="Implement"].Mui-selected': {
												bgcolor: 'warning.main',
												color: 'warning.contrastText',
												boxShadow: `0 2px 5px ${alpha(theme.palette.warning.main, 0.28)}`,
												'&:hover': {
													bgcolor: 'warning.dark',
													color: 'warning.contrastText',
												},
											},
									},
								})}
							>
								<ToggleButton value="Design">Design</ToggleButton>
								<ToggleButton value="Implement">Implement</ToggleButton>
								</ToggleButtonGroup>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto' }}>
								{supportsSpeechRecognition && (
									<Box
										sx={(theme) => ({
											display: 'flex',
											alignItems: 'center',
											height: 32,
											flexShrink: 0,
											border: '1px solid',
											borderColor: isListening ? alpha(theme.palette.error.main, 0.65) : 'divider',
											borderRadius: 4,
											bgcolor: isListening ? alpha(theme.palette.error.main, 0.05) : 'transparent',
										})}
									>
										<Tooltip title="Transcription language">
											<Button
												aria-label="Choose transcription language"
												aria-controls={speechLanguageAnchorEl ? 'speech-language-menu' : undefined}
												aria-expanded={speechLanguageAnchorEl ? 'true' : undefined}
												onClick={(event) => setSpeechLanguageAnchorEl(event.currentTarget)}
												disabled={sending || isPolling}
												size="small"
												startIcon={<LanguageIcon fontSize="small" />}
												sx={{
													minWidth: 0,
													height: 30,
													px: 0.75,
													color: 'text.secondary',
													textTransform: 'none',
													borderRadius: '15px 0 0 15px',
													borderRight: '1px solid',
													borderColor: 'divider',
												}}
											>
												{speechLanguage.split('-')[0].toUpperCase()}
											</Button>
										</Tooltip>
										<Menu
											id="speech-language-menu"
											anchorEl={speechLanguageAnchorEl}
											open={Boolean(speechLanguageAnchorEl)}
											onClose={() => setSpeechLanguageAnchorEl(null)}
										>
											{speechRecognitionLanguageOptions.map((language) => (
												<MenuItem
													key={language.code}
													selected={speechLanguage === language.code}
													onClick={() => {
														setSpeechLanguage(language.code);
														stopSpeechRecognition();
														setSpeechLanguageAnchorEl(null);
													}}
												>
													{language.label}
												</MenuItem>
											))}
										</Menu>
										<Tooltip title={isListening ? 'Stop voice transcription' : 'Transcribe with microphone'}>
											<IconButton
											aria-label={isListening ? 'Stop voice transcription' : 'Start voice transcription'}
											aria-pressed={isListening}
											onClick={toggleSpeechRecognition}
											disabled={sending || isPolling}
											size="small"
											sx={(theme) => ({
													width: 30,
													height: 30,
												color: isListening ? 'error.main' : 'text.secondary',
												bgcolor: isListening ? alpha(theme.palette.error.main, 0.2) : 'transparent',
												position: 'relative',
												overflow: 'visible',
												borderRadius: '0 15px 15px 0',
												'&::after': isListening
													? {
														content: '""',
														position: 'absolute',
														inset: -3,
														borderRadius: '50%',
														border: `2px solid ${alpha(theme.palette.error.main, 0.9)}`,
														animation: `${activeMicrophoneHalo} 1.15s ease-out infinite`,
														pointerEvents: 'none',
													}
													: undefined,
												'& .MuiSvgIcon-root': {
													position: 'relative',
													zIndex: 1,
												},
												'&:hover': {
													color: isListening ? 'error.dark' : 'text.primary',
													bgcolor: isListening ? alpha(theme.palette.error.main, 0.16) : 'action.hover',
												},
											})}
										>
											<MicIcon fontSize="small" />
										</IconButton>
										</Tooltip>
									</Box>
								)}
								{input.length > 0 && <Tooltip title="Clear prompt">
									<span>
										<IconButton
											aria-label="Clear prompt"
											onClick={() => {
												stopSpeechRecognition();
												setInput('');
												setMentionQuery(null);
												setMentionSuggestions([]);
												inputRef.current?.focus();
											}}
											disabled={sending || isPolling}
											size="small"
											sx={{ ...composerIconButtonSx, '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
										>
											<CloseIcon fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>}
								{isPolling ? (
									<Tooltip title={adapter.cancelResponse ? (stopPending ? 'Stopping agent…' : responseStatus === CHAT_RESPONSE.stopping ? 'Retry stop request' : 'Stop agent') : 'Stop waiting for the response'}>
										<IconButton
											aria-label={adapter.cancelResponse ? 'Stop agent' : 'Stop waiting for the response'}
											aria-busy={true}
											onClick={stopAgent}
											disabled={!!adapter.cancelResponse && (stopPending || !responseTurnId)}
											size="small"
											sx={(theme) => ({
												...progressingComposerActionSx(theme.palette.warning.main),
												width: 32,
												height: 32,
												borderRadius: '50%',
												border: '1px solid',
												borderColor: 'warning.main',
												bgcolor: 'warning.main',
												color: 'warning.contrastText',
												boxShadow: (theme) => `0 4px 12px ${theme.palette.warning.main}66`,
												transition: (theme) =>
													theme.transitions.create(['background-color', 'box-shadow', 'transform'], { duration: theme.transitions.duration.shortest }),
												'&:hover': {
													bgcolor: 'warning.dark',
													transform: 'translateY(-1px)',
													boxShadow: (theme) => `0 6px 16px ${theme.palette.warning.main}88`,
												},
											})}
										>
												<StopIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								) : (
									<IconButton
										title="Send (Ctrl + Enter)"
										aria-label="Send message"
										aria-busy={sending}
										onClick={handleSend}
										disabled={
											(!input.trim() && !pendingAttachments.some((pending) => pending.status === 'uploaded'))
											|| pendingAttachments.some((pending) => pending.status !== 'uploaded')
											|| sending
										}
										size="small"
										color="primary"
										sx={(theme) => ({
											...(sending ? progressingComposerActionSx(theme.palette.primary.main) : {}),
											width: 32,
											height: 32,
											borderRadius: '50%',
											border: 0,
											bgcolor: 'primary.main',
											color: 'primary.contrastText',
											'&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
											transition: (theme) =>
												theme.transitions.create(['background-color', 'box-shadow', 'transform'], { duration: theme.transitions.duration.shortest }),
											'@media (hover: hover)': {
												'&:not(.Mui-disabled):hover': { bgcolor: 'primary.dark' },
											},
										})}
									>
										<ArrowUpwardIcon fontSize="small" />
									</IconButton>
								)}
							</Box>
						</Box>
					</Paper>
				</Box>
			)}
		</>
	);

	const chatHeaderActions = <>
				{(isPolling || sending) && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
				{(adapter.isAvailable || headerPrimaryActions) && (
					<>
						<Box
							role="group"
							aria-label="Chat actions"
							sx={{
								display: 'flex',
								alignItems: 'center',
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: appShape.controlGroupRadius,
								overflow: 'hidden',
								flexShrink: 0,
							}}
						>
							{adapter.isAvailable && (headerRenaming && !hasConversationNameBar ? (
								<>
									<Tooltip title="Save name">
										<span>
											<IconButton
												size="small"
												aria-label="Save conversation name"
												onClick={saveCurrentSessionRename}
												disabled={!canRenameCurrentSession || headerRenameSaving || sending || isPolling}
												sx={headerActionSx}
											>
												{headerRenameSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
											</IconButton>
										</span>
									</Tooltip>
									<Tooltip title="Cancel rename">
										<span>
											<IconButton
												size="small"
												aria-label="Cancel conversation rename"
												onClick={cancelCurrentSessionRename}
												disabled={headerRenameSaving}
												sx={headerActionSx}
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										</span>
									</Tooltip>
								</>
							) : (
								<>
									<Tooltip title={startNewSessionTitle}>
										<span>
											<IconButton
												size="small"
												aria-label="Start new conversation"
												onClick={startNewSession}
												disabled={!canStartNewSession}
												sx={{ ...headerActionSx, color: 'primary.main', '&:hover': { bgcolor: 'primary.50', color: 'primary.main' } }}
											>
												<AddIcon fontSize="small" />
											</IconButton>
										</span>
									</Tooltip>
									{adapter.listSessions && (
										<Tooltip title="Switch conversation">
											<span>
												<IconButton size="small" aria-label="View conversation history" onClick={openSessionPicker} disabled={!canChangeSession} sx={headerActionSx}>
													<HistoryIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									)}
									{!chatDialogOpen && headerPrimaryActions}
									{hasHeaderOverflowActions && (
										<Tooltip title="More conversation actions">
											<IconButton
												size="small"
												aria-label="More conversation actions"
												onClick={(event) => setHeaderMenuAnchorEl(event.currentTarget)}
												sx={headerActionSx}
											>
												<MoreVertIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									)}
								</>
							))}
							{!adapter.isAvailable && !chatDialogOpen && headerPrimaryActions}
						</Box>
						<Menu
							anchorEl={headerMenuAnchorEl}
							open={Boolean(headerMenuAnchorEl)}
							onClose={closeHeaderMenu}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						>
							{!hasConversationNameBar && adapter.renameSession && hasCurrentSession && (
								<MenuItem
									onClick={() => {
										closeHeaderMenu();
										startCurrentSessionRename();
									}}
									disabled={!canRenameCurrentSession || sending || isPolling}
								>
									<ListItemIcon>
										<EditIcon fontSize="small" />
									</ListItemIcon>
									<ListItemText>Rename conversation</ListItemText>
								</MenuItem>
							)}
							{adapter.addParticipants && hasCurrentSession && (
								<MenuItem
									onClick={() => {
										closeHeaderMenu();
										openShareDialog();
									}}
									disabled={sending || isPolling}
								>
									<ListItemIcon>
										<GroupAddIcon fontSize="small" />
									</ListItemIcon>
									<ListItemText>Share conversation</ListItemText>
								</MenuItem>
							)}
							{hasCurrentSession && (
								<MenuItem
									onClick={() => {
										closeHeaderMenu();
										void refreshCurrentSession();
									}}
									disabled={!canRefreshCurrentSession || sending}
								>
									<ListItemIcon>{refreshingSession ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}</ListItemIcon>
									<ListItemText>Refresh conversation</ListItemText>
								</MenuItem>
							)}
						</Menu>
					</>
				)}
	</>;

	const chatWindowActions = !chatDialogOpen && (adapter.isAvailable || headerActions) && (
		<Box role="group" aria-label="Chat window controls" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.5, ml: 1 }}>
			{adapter.isAvailable && <>
				<Tooltip title="Open chat in full screen"><span>
					<IconButton size="small" aria-label="Open chat in full screen" onClick={openChatDialog} disabled={sessionLoading} sx={headerActionSx}>
						<OpenInFullIcon fontSize="small" />
					</IconButton>
				</span></Tooltip>
				{onDismiss && <Tooltip title="Collapse chat">
					<IconButton size="small" aria-label="Collapse chat" onClick={onDismiss} sx={headerActionSx}><CollapseIcon fontSize="small" /></IconButton>
				</Tooltip>}
			</>}
			{headerActions}
		</Box>
	);

	const chatSurface = (
		<Box
			sx={(theme) => ({
				bgcolor: 'background.paper',
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				height: '100%',
				minHeight: 0,
				overflow: 'hidden',
				minWidth: 0,
				boxSizing: 'border-box',
				border: 1,
				borderColor: 'divider',
				borderRadius: appShape.surfaceRadius,
				boxShadow: `0 6px 22px ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.2 : 0.08)}`,
			})}
		>
			{/* Expanded dialogs use their existing title bar instead of a second header. */}
			{!chatDialogOpen && <Box
				component="header"
				sx={[{
					display: 'flex',
					alignItems: 'center',
					pl: 2,
					pr: 1,
					py: 0.5,
					flexShrink: 0,
					bgcolor: 'background.paper',
					backgroundImage: 'var(--Paper-overlay, none)',
					borderBottom: 1,
					borderColor: 'divider',
				}, ...(Array.isArray(headerSx) ? headerSx : [headerSx])]}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', mr: 1, flexShrink: 0 }}>{renderAssistantAvatar(24, true)}</Box>
				<Box sx={{ flex: 1, minWidth: 0, mr: 0.5 }}>
					{headerRenaming && !hasConversationNameBar ? (
						<TextField
							value={headerRenameName}
							onChange={(event) => setHeaderRenameName(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									event.preventDefault();
									saveCurrentSessionRename();
								}
								if (event.key === 'Escape') {
									cancelCurrentSessionRename();
								}
							}}
							size="small"
							variant="standard"
							fullWidth
							autoFocus
							disabled={headerRenameSaving}
							slotProps={{ input: { disableUnderline: true } }}
							sx={{ '& input': { fontSize: '0.875rem', fontWeight: 500, color: 'primary.main' } }}
						/>
					) : (
						<Typography
							variant="body2"
							title={headerTitle}
							noWrap
							sx={{
								color: 'text.primary',
								fontWeight: 700,
								minWidth: 0,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{headerTitle}
						</Typography>
					)}
					{selectedAgent?.name && !showAgentNameInHeader && (
						<Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
							{selectedAgent.name}
						</Typography>
					)}
				</Box>
				{chatHeaderActions}
				{chatWindowActions}
			</Box>}

			{hasConversationNameBar && <Box role="group" aria-label="Conversation name"
				sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, minHeight: 36, flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
				{headerRenaming ? <>
					<TextField value={headerRenameName} onChange={(event) => setHeaderRenameName(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') { event.preventDefault(); void saveCurrentSessionRename(); }
							if (event.key === 'Escape') { event.preventDefault(); cancelCurrentSessionRename(); }
						}}
						size="small" variant="standard" fullWidth autoFocus disabled={headerRenameSaving}
						slotProps={{ htmlInput: { 'aria-label': 'Conversation name' } }} sx={{ minWidth: 0 }} />
					<Tooltip title="Save name"><span><IconButton size="small" aria-label="Save conversation name" onClick={() => void saveCurrentSessionRename()}
						disabled={!canRenameCurrentSession || !headerRenameName.trim() || headerRenameSaving || sending || isPolling}>
						{headerRenameSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
					</IconButton></span></Tooltip>
					<Tooltip title="Cancel rename"><span><IconButton size="small" aria-label="Cancel conversation rename" onClick={cancelCurrentSessionRename} disabled={headerRenameSaving}>
						<CloseIcon fontSize="small" />
					</IconButton></span></Tooltip>
				</> : <>
					<Typography variant="body2" noWrap title={sessionName?.trim() || 'New conversation'} sx={{ flex: 1, minWidth: 0 }}>
						{sessionName?.trim() || 'New conversation'}
					</Typography>
					<Tooltip title="Rename conversation"><span><IconButton size="small" aria-label="Edit conversation name" onClick={startCurrentSessionRename}
						disabled={!canRenameCurrentSession || sending || isPolling}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
				</>}
			</Box>}

			{content}
		</Box>
	);

	return (
		<>
			{chatDialogOpen ? (
				<Dialog
					open
					onClose={closeChatDialog}
					fullScreen={chatDialogMaximized}
					fullWidth
					maxWidth={false}
					PaperProps={{
						sx: chatDialogMaximized
							? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', overflow: 'hidden' }
							: { width: 'min(1120px, 95vw)', height: 'min(860px, 92vh)', maxWidth: '95vw', maxHeight: '92vh', overflow: 'hidden' },
					}}
				>
					<DialogTitle aria-label={headerTitle} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: avatarAgentId ? 0.5 : 1 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
							{renderAssistantAvatar(24, true)}
						</Box>
						<Typography component="span" variant="h6" noWrap sx={{ minWidth: 0, flex: 1 }}>
							{headerTitle}
						</Typography>
						{chatHeaderActions}
						<DialogHeaderActions
							isMaximized={chatDialogMaximized}
							onToggleMaximized={() => setChatDialogMaximized((value) => !value)}
							onClose={closeChatDialog}
						/>
					</DialogTitle>
					<DialogContent dividers sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', p: 0 }}>
						{chatSurface}
					</DialogContent>
				</Dialog>
			) : (
				chatSurface
			)}

			{attachmentPreview && (
				<ChatAttachmentPreviewDialog
					selection={attachmentPreview.selection}
					resolveUrl={attachmentPreview.resolveUrl}
					onClose={() => setAttachmentPreview(null)}
				/>
			)}

			<Dialog open={sessionPickerOpen} onClose={() => setSessionPickerOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Previous Conversations</DialogTitle>
				<DialogContent dividers onScroll={handleSessionPickerScroll} sx={{ p: 0 }}>
					{sessionPickerError && (
						<Alert severity="error" sx={{ m: 2, mb: sessionPickerLoading || sessions.length === 0 ? 2 : 0 }}>
							{sessionPickerError}
						</Alert>
					)}
					{deleteConfirmSession && (
						<Alert
							severity="warning"
							sx={{ m: 2, mb: sessionPickerLoading || sessions.length === 0 ? 2 : 0 }}
							action={
								<Box sx={{ display: 'flex', gap: 0.5 }}>
									<Button size="small" onClick={cancelDeleteSession} disabled={!!deletingSessionId}>
										Cancel
									</Button>
									<Button size="small" color="error" onClick={() => confirmDeleteSession(deleteConfirmSession)} disabled={!!deletingSessionId}>
										{deletingSessionId === deleteConfirmSession.sessionId ? 'Deleting' : 'Delete'}
									</Button>
								</Box>
							}
						>
							Delete "{deleteConfirmSession.name}"?
						</Alert>
					)}
					{sessionPickerLoading && sessions.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
							<CircularProgress size={22} />
						</Box>
					) : sessions.length === 0 ? (
						<Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
							No previous conversations.
						</Typography>
					) : (
						<List disablePadding>
							{sessions.map((session) => {
								const isCurrent = session.sessionId === currentSessionIdRef.current;
								const createdDate = formatTimestampFull(session.createdDate);
								const isRenaming = renamingSessionId === session.sessionId;
								if (isRenaming) {
									return (
										<ListItem key={session.sessionId} divider sx={{ py: 1, pl: 2, pr: 1, gap: 1 }}>
											<TextField
												value={renamingName}
												onChange={(event) => setRenamingName(event.target.value)}
												onKeyDown={(event) => {
													if (event.key === 'Enter') {
														event.preventDefault();
														saveRenameSession(session);
													}
													if (event.key === 'Escape') {
														cancelRenameSession();
													}
												}}
												size="small"
												fullWidth
												autoFocus
												disabled={renamingSaving}
											/>
											<Tooltip title="Save name">
												<span>
													<IconButton size="small" color="primary" onClick={() => saveRenameSession(session)} disabled={renamingSaving}>
														{renamingSaving ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
													</IconButton>
												</span>
											</Tooltip>
											<Tooltip title="Cancel">
												<span>
													<IconButton size="small" onClick={cancelRenameSession} disabled={renamingSaving}>
														<CloseIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</ListItem>
									);
								}

								return (
									<ListItem
										key={session.sessionId}
										disablePadding
										divider
										secondaryAction={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
												{adapter.renameSession && (
													<Tooltip title="Rename conversation">
														<span>
															<IconButton size="small" onClick={() => startRenameSession(session)} disabled={renamingSaving || !!deletingSessionId}>
																<EditIcon fontSize="small" />
															</IconButton>
														</span>
													</Tooltip>
												)}
												{adapter.deleteSession && (
													<Tooltip title="Delete conversation">
														<span>
															<IconButton
																edge="end"
																size="small"
																color={deleteConfirmSessionId === session.sessionId ? 'error' : 'default'}
																onClick={() => startDeleteSession(session)}
																disabled={renamingSaving || !!deletingSessionId}
															>
																{deletingSessionId === session.sessionId ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
															</IconButton>
														</span>
													</Tooltip>
												)}
											</Box>
										}
									>
										<ListItemButton selected={isCurrent} onClick={() => handleSelectSession(session)}>
											<ListItemText
												primary={session.name}
												secondary={session.agentName ? `${session.agentName} - ${createdDate}` : createdDate}
												primaryTypographyProps={{ variant: 'body2', noWrap: true }}
												secondaryTypographyProps={{ variant: 'caption' }}
											/>
										</ListItemButton>
									</ListItem>
								);
							})}
							{sessionPickerLoading && (
								<ListItem sx={{ justifyContent: 'center', py: 1.5 }}>
									<CircularProgress size={18} />
								</ListItem>
							)}
						</List>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSessionPickerOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={shareDialogOpen} onClose={() => !shareSaving && setShareDialogOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<ShareIcon color="primary" fontSize="small" />
					Share Conversation
				</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Add people to this conversation. They can open it and continue with the selected agent.
					</Typography>
					{shareError && <Alert severity="error" sx={{ mb: 2 }}>{shareError}</Alert>}
					{(sharedParticipantsLoading || sharedParticipants.length > 0) && (
						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle2">Already shared with</Typography>
							{sharedParticipantsLoading ? (
								<CircularProgress size={16} sx={{ mt: 0.75 }} />
							) : (
								<Box>
									{sharedParticipants.map((participant) => (
										<Box key={participant.userId} sx={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
											<Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
												{participant.name}
											</Typography>
											{participant.canRemove && adapter.removeParticipant && (
												<Tooltip title="Remove access">
													<span>
														<IconButton
															size="small"
															aria-label={`Remove ${participant.name} from this conversation`}
															onClick={() => void removeSharedParticipant(participant)}
															disabled={removingParticipantId !== null}
															color="error"
														>
															{removingParticipantId === participant.userId ? <CircularProgress size={16} color="inherit" /> : <PersonRemoveIcon fontSize="small" />}
														</IconButton>
													</span>
												</Tooltip>
											)}
										</Box>
									))}
								</Box>
							)}
						</Box>
					)}
					<Autocomplete
						multiple
						options={getShareUserOptions(shareUsers, selectedShareUsers, sharedParticipants)}
						value={selectedShareUsers}
						onChange={(_, value) => setSelectedShareUsers(removeSharedUsers(value, sharedParticipants))}
						onInputChange={(_, value, reason) => {
							if (reason === 'input' || reason === 'clear') {
								setShareUserSearchText(value);
							}
						}}
						loading={shareUsersLoading}
						filterOptions={(options) => options}
						getOptionLabel={getTenantUserLabel}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						loadingText="Searching people..."
						noOptionsText="No matching people"
						renderOption={(props, option) => (
							<li {...props} key={option.id}>
								<Box>
									<Typography variant="body2">{getTenantUserName(option)}</Typography>
									<Typography variant="caption" color="text.secondary">{option.email}</Typography>
								</Box>
							</li>
						)}
						renderInput={(params) => <TextField {...params} label="People" placeholder="Search people" />}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShareDialogOpen(false)} disabled={shareSaving}>Cancel</Button>
					<Button variant="contained" onClick={shareCurrentSession} disabled={shareSaving || selectedShareUsers.length === 0}>
						{shareSaving ? 'Sharing...' : 'Share'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

function getTenantUserName(user: TenantUserModel): string {
	return user.displayName?.trim() || [user.givenName, user.surname].filter(Boolean).join(' ').trim() || user.email;
}

function getTenantUserLabel(user: TenantUserModel): string {
	const name = getTenantUserName(user);
	return name === user.email ? user.email : `${name} (${user.email})`;
}

function compareTenantUsers(left: TenantUserModel, right: TenantUserModel): number {
	return getTenantUserLabel(left).localeCompare(getTenantUserLabel(right), undefined, { sensitivity: 'base' });
}

function mergeShareUserOptions(users: TenantUserModel[], selectedUsers: TenantUserModel[]): TenantUserModel[] {
	const usersById = new Map(users.map((user) => [user.id, user]));
	for (const user of selectedUsers) {
		usersById.set(user.id, user);
	}

	return Array.from(usersById.values()).sort(compareTenantUsers);
}

function getShareUserOptions(
	users: TenantUserModel[],
	selectedUsers: TenantUserModel[],
	sharedParticipants: ChatParticipant[],
): TenantUserModel[] {
	return removeSharedUsers(mergeShareUserOptions(users, selectedUsers), sharedParticipants);
}

function removeSharedUsers(users: TenantUserModel[], sharedParticipants: ChatParticipant[]): TenantUserModel[] {
	const sharedUserIds = new Set(sharedParticipants.map((participant) => participant.userId));
	return users.filter((user) => !sharedUserIds.has(user.id));
}

export default PromptChat;
